import React, { useState, useMemo } from 'react';
import {
  X,
  CalendarDays,
  Sparkles,
  MapPin,
  Route,
  ExternalLink,
  Copy,
  Check,
  Flame,
  Moon,
  Compass,
  Utensils,
  Eye,
  Info,
  Calendar,
  ChevronRight,
  HelpCircle,
  Sliders,
  CheckCircle2,
  TreePine,
  Landmark,
  Building2,
  Waves,
} from 'lucide-react';
import { Spot, Language, UserLocation } from '../types/spot';
import { SPOTS_DATA } from '../data/spotsData';
import { generateMultiSpotRouteUrl, getSpotMapLinks } from '../services/mapLinks';
import confetti from 'canvas-confetti';

interface WeekendPlannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  userLocation: UserLocation;
  savedSpotIds: string[];
  onImportSpotsToPlanner: (spotIds: string[]) => void;
  onSelectSpot: (spot: Spot) => void;
}

type Weekday = 'fri' | 'sat' | 'sun';
type MonthFilter = 'all' | 'sep' | 'oct' | 'nov' | 'dec';
type RegionFilter = 'all' | 'seoul' | 'incheon' | 'gyeonggi';
type ThemeFilter = 'all' | 'festivals' | 'palaces' | 'modern' | 'nature' | 'food';
type ActiveTab = 'curated' | 'generator' | 'guide';

interface DaySchedule {
  dayNameDe: string;
  dayNameEn: string;
  dayNameKr: string;
  dateFormattedDe?: string;
  dateFormattedEn?: string;
  dateFormattedKr?: string;
  themeTitleDe: string;
  themeTitleEn: string;
  themeTitleKr: string;
  spotIds: string[];
  schedule: {
    time: string;
    titleDe: string;
    titleEn: string;
    titleKr: string;
    spotId?: string;
    tipDe: string;
    tipEn: string;
    tipKr: string;
    isSoloFood?: boolean;
  }[];
}

interface CuratedWeekend {
  id: string;
  titleDe: string;
  titleEn: string;
  titleKr: string;
  dateRange: string;
  month: 'sep' | 'oct' | 'nov' | 'dec';
  badgeDe: string;
  badgeEn: string;
  badgeKr: string;
  badgeType: 'fireworks' | 'event' | 'foliage' | 'coast' | 'winter';
  descriptionDe: string;
  descriptionEn: string;
  descriptionKr: string;
  region: 'Seoul' | 'Incheon' | 'Gyeonggi' | 'Mixed';
  soloHighlightDe: string;
  soloHighlightEn: string;
  soloHighlightKr: string;
  days: Record<Weekday, DaySchedule>;
}

// 9 Curated Master Weekends from September to December 2026
const CURATED_WEEKENDS: CuratedWeekend[] = [
  {
    id: 'weekend-1-fireworks',
    titleDe: 'Wochenende 1: Seoul International Fireworks Festival & Han-Fluss Skyline',
    dateRange: '04.09. – 06.09.2026',
    titleEn: 'Weekend 1: Seoul International Fireworks Festival & River Skyline',
    titleKr: '주말 1: 서울세계불꽃축제 & 한강 스카이라인',
    month: 'sep',
    badgeDe: '🎆 Mega-Event (05.09.2026)',
    badgeEn: '🎆 Mega-Event (Sep 5, 2026)',
    badgeKr: '🎆 메가 축제 (2026.09.05)',
    badgeType: 'fireworks',
    region: 'Seoul',
    descriptionDe: 'Das spektakulärste Pyro-Event des Jahres am Han-Fluss kombiniert mit modernem Shopping & goldenem Sonnenuntergang.',
    descriptionEn: 'The most spectacular pyro show of the year on the Han River paired with modern architecture and golden sunset.',
    descriptionKr: '한강에서 펼쳐지는 1년에 단 한 번뿐인 초대형 불꽃축제와 여의도 스카이라인.',
    soloHighlightDe: 'Perfekt für Solo: Picknickmatte im Hangang Park ausbreiten, Kiosk-Ramen am Automaten kochen & entspannt die Show genießen.',
    soloHighlightEn: 'Solo Tip: Grab a foil bowl ramen from convenience store, sit with a picnic mat on the river grass.',
    soloHighlightKr: '혼놀 꿀팁: 한강 편의점 끓인 라면 + 돗자리로 편안하게 불꽃놀이 감상.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Yeouido Moderne & Hangang Sunset',
        dateFormattedDe: 'Freitag, 04.09.2026',
        dateFormattedEn: 'Friday, Sep 4, 2026',
        dateFormattedKr: '2026.09.04 (금)',
        dayNameEn: 'Friday: Yeouido Modern & Hangang Sunset',
        dayNameKr: '금요일: 여의도 도심 & 한강 일몰',
        themeTitleDe: 'Moderne Architektur, Gourmet-Hallen & Fluss-Vibes',
        themeTitleEn: 'Modern Architecture, Gourmet Halls & River Vibes',
        themeTitleKr: '현대 건축 & 고메 홀 & 한강 정취',
        spotIds: ['seoul-63-building', 'seoul-yeouido-hangang-park'],
        schedule: [
          {
            time: '14:00 – 17:00',
            titleDe: 'The Hyundai Seoul & Sounds Forest Indoor-Garten',
            titleEn: 'The Hyundai Seoul & Sounds Forest Indoor Garden',
            titleKr: '더현대 서울 & 사운즈 포레스트',
            tipDe: 'Spektakuläre futuristische Architektur. Im Untergeschoss B1 gibt es grandiose Solo-Food-Stände (Honbap-Theken).',
            tipEn: 'Futuristic mall. Basement 1 has dozens of single-diner counter spots for lunch.',
            tipKr: '지하 1층에 1인 혼밥하기 좋은 프리미엄 푸드코트가 가득합니다.',
            isSoloFood: true,
          },
          {
            time: '17:30 – 19:30',
            titleDe: '63 Building & Panorama-Aussicht',
            titleEn: '63 Building Golden Skyscraper',
            titleKr: '63스퀘어 63빌딩',
            spotId: 'seoul-63-building',
            tipDe: 'Goldglänzender Wolkenkratzer mit gläsernem Panorama-Aufzug und Blick auf den Sonnenuntergang.',
            tipEn: 'Glass elevators soaring up to 60F sky art gallery over Han River.',
            tipKr: '통유리 엘리베이터를 타고 한강 일몰을 한눈에 조망하세요.',
          },
          {
            time: '19:30 – 21:30',
            titleDe: 'Yeouido Hangang Park & Instant-Ramen Picknick',
            titleEn: 'Yeouido Hangang Park Night Picnic',
            titleKr: '여의도 한강공원 야간 피크닉',
            spotId: 'seoul-yeouido-hangang-park',
            tipDe: 'Kultige Tradition: An den Kochautomaten im Convenience Store "Hangang Ramen" kochen und am Wasser essen.',
            tipEn: 'Seoul tradition: Cook foil bowl ramen at the riverside machines and watch city lights.',
            tipKr: '편의점 은박지 한강 라면을 끓여 강변에서 야경을 만끽하세요.',
            isSoloFood: true,
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Der große Feuerwerkstag (05. September 2026)',
        dateFormattedDe: 'Samstag, 05.09.2026 (🎆 Feuerwerk-Hauptshow)',
        dateFormattedEn: 'Saturday, Sep 5, 2026 (🎆 Fireworks Festival)',
        dateFormattedKr: '2026.09.05 (토) 🎆 서울세계불꽃축제',
        dayNameEn: 'Saturday: Seoul International Fireworks Festival (Sep 5, 2026)',
        dayNameKr: '토요일: 서울세계불꽃축제 (2026.09.05 토)',
        themeTitleDe: '100.000 Feuerwerksraketen, Drohnenshow & Weltklasse-Pyrotechnik',
        themeTitleEn: '100,000 fireworks, drone formations & international pyro teams',
        themeTitleKr: '10만 발의 불꽃과 드론쇼가 수놓는 밤하늘',
        spotIds: ['event-seoul-international-fireworks', 'seoul-yeouido-hangang-park'],
        schedule: [
          {
            time: '13:00 – 16:00',
            titleDe: 'Ankunft & Platzsicherung am Flussufer',
            titleEn: 'Arrival & River Lawn Spot Setup',
            titleKr: '여의도 한강공원 도착 및 자리 잡기',
            tipDe: 'Tipp für Solo-Reisende: Suche dir einen Platz auf der Liegewiese im Yeouido Park oder auf der Nodeul-Insel vor 16:00 Uhr.',
            tipEn: 'Solo tip: Secure a lawn spot before 16:00. Bring a folding mat, power bank, and water.',
            tipKr: '오후 4시 전 돗자리를 펴고 자리 잡기. 보조배터리와 겉옷 필수!',
          },
          {
            time: '16:00 – 19:30',
            titleDe: 'Vor-Programm, Street Food & Musik',
            titleEn: 'Pre-Festival Busking & Street Food',
            titleKr: '사전 행사 & 푸드존 즐기기',
            tipDe: 'Live-Bühnen, Foodtrucks und Musik entlang der Uferpromenade.',
            tipEn: 'Live music stages and festival food trucks along the promenade.',
            tipKr: '한강변을 따라 푸드트럭과 버스킹 공연을 즐겨보세요.',
            isSoloFood: true,
          },
          {
            time: '20:00 – 21:10',
            titleDe: '🎆 Die Haupt-Feuerwerksshow (Südkorea, USA, Europa)',
            titleEn: '🎆 Main Pyro Spectacle (Over 100,000 fireworks)',
            titleKr: '🎆 본 불꽃쇼 (한국·미국·유럽 참가)',
            spotId: 'event-seoul-international-fireworks',
            tipDe: 'Gigantische Raketenexplosionen synchron zu Musik und Lichtprojektionen über der Wonhyo-Brücke.',
            tipEn: 'Massive explosive fireworks synchronized to music over Han River.',
            tipKr: '원효대교와 한강 상공을 화려하게 수놓는 환상적인 불꽃과 드론쇼.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Saetgang Ökopark & Nodeul Island Chill',
        dateFormattedDe: 'Sonntag, 06.09.2026',
        dateFormattedEn: 'Sunday, Sep 6, 2026',
        dateFormattedKr: '2026.09.06 (일)',
        dayNameEn: 'Sunday: Saetgang Eco Park & Nodeul Island',
        dayNameKr: '일요일: 샛강생태공원 & 노들섬 힐링',
        themeTitleDe: 'Entspannter Natur-Ausklang nach dem Festival',
        themeTitleEn: 'Relaxed nature walk following the fireworks',
        themeTitleKr: '축제 후 여유로운 자연 산책',
        spotIds: ['seoul-yeouido-hangang-park'],
        schedule: [
          {
            time: '11:00 – 14:00',
            titleDe: 'Saetgang Öko-Park Holzstege',
            titleEn: 'Saetgang Ecological Park Boardwalks',
            titleKr: '여의도 샛강생태공원 산책로',
            tipDe: 'Wunderschöne Holzstege durch Schilf und Weidenwälder mitten in Yeouido – pure Ruhe.',
            tipEn: 'Wooden boardwalks weaving through wetlands and reeds in central Yeouido.',
            tipKr: '빌딩 숲 사이 자연 그대로 보존된 조용한 생태공원 산책.',
          },
        ],
      },
    },
  },
  {
    id: 'weekend-2-royal-palace',
    titleDe: 'Wochenende 2: Königliches Seoul & Gyeongbokgung Nachtöffnung',
    dateRange: '11.09. – 13.09.2026',
    titleEn: 'Weekend 2: Royal Seoul & Gyeongbokgung Night Opening',
    titleKr: '주말 2: 궁궐의 밤 & 경복궁 야간관람',
    month: 'sep',
    badgeDe: '🏯 Palast-Nachtzauber',
    badgeEn: '🏯 Palace Night Magic',
    badgeKr: '🏯 궁궐 야간개장',
    badgeType: 'event',
    region: 'Seoul',
    descriptionDe: 'Erlebe den königlichen Hauptpalast im Mondschein und schlendere durch 600 Jahre alte Hanok-Gassen.',
    descriptionEn: 'Witness royal palaces lit by moonlight and stroll through historic 600-year-old Hanok alleys.',
    descriptionKr: '달빛 아래 빛나는 경복궁과 고즈넉한 북촌 한옥마을, 광장시장 먹거리.',
    soloHighlightDe: 'Solo-Essen: Mungbohnen-Pancakes (Bindaetteok) und Kalguksu-Nudeln am Gwangjang Markt an offenen Theken.',
    soloHighlightEn: 'Solo Food: Sizzling bindaetteok and kalguksu noodle bars at Gwangjang Market.',
    soloHighlightKr: '혼밥 추천: 광장시장 즉석 칼국수 및 녹두 빈대떡 1인분 포차.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Nachtöffnung Gyeongbokgung & Dachterrasse',
        dateFormattedDe: 'Freitag, 11.09.2026 (🏯 Gyeongbokgung Nacht)',
        dateFormattedEn: 'Friday, Sep 11, 2026 (🏯 Palace Night)',
        dateFormattedKr: '2026.09.11 (금) 🏯 경복궁 야간관람',
        dayNameEn: 'Friday: Gyeongbokgung Night Tour & Sky Deck',
        dayNameKr: '금요일: 경복궁 야간관람 & 역사박물관 옥상',
        themeTitleDe: 'Paläste im Abendlicht & Mondschein-Illumination',
        themeTitleEn: 'Palace sunset vistas & moonlit illumination',
        themeTitleKr: '노을 지는 궁궐과 달빛 야간투어',
        spotIds: ['seoul-history-museum-rooftop', 'event-gyeongbokgung-night-tour'],
        schedule: [
          {
            time: '16:00 – 18:00',
            titleDe: 'Nationalmuseum für Zeitgeschichte 8F Dachterrasse',
            titleEn: 'National Museum of Contemporary History 8F Roof Garden',
            titleKr: '대한민국역사박물관 8층 옥상정원',
            spotId: 'seoul-history-museum-rooftop',
            tipDe: '100% kostenlos! Bester Frontalblick von oben auf das Gwanghwamun-Tor und den Berg Bugaksan.',
            tipEn: '100% free rooftop garden with stunning frontal views of Gwanghwamun Gate.',
            tipKr: '광화문과 경복궁, 북악산이 한눈에 내려다보이는 무료 옥상 전망대.',
          },
          {
            time: '18:00 – 19:00',
            titleDe: 'Solo-Dinner: Samcheongdong Sujebi',
            titleEn: 'Solo Dinner: Samcheongdong Sujebi',
            titleKr: '삼청동 수제비 혼밥',
            tipDe: 'Michelin Bib Gourmand Teigfleckerlsuppe mit Muscheln. Perfekt für Alleinesser.',
            tipEn: 'Michelin Bib Gourmand hand-torn noodle soup. Welcoming for solo diners.',
            tipKr: '따끈하고 쫄깃한 수제비 1인분으로 든든한 저녁 식사.',
            isSoloFood: true,
          },
          {
            time: '19:00 – 21:30',
            titleDe: 'Gyeongbokgung Palast Nachtöffnung & Lichterzauber',
            titleEn: 'Gyeongbokgung Palace Night Opening',
            titleKr: '경복궁 야간관람',
            spotId: 'event-gyeongbokgung-night-tour',
            tipDe: 'Der Gyeonghoeru-Pavillon spiegelt sich im beleuchteten Teich. 300 Ausländer-Tickets tägl. an der Kasse (oder gratis im Hanbok).',
            tipEn: 'Gyeonghoeru pavilion floating on illuminated lotus pond. 300 daily foreigner tickets or free with Hanbok.',
            tipKr: '연못에 비치는 경회루의 은은한 조명. 외국인 현장 발권 300매 또는 한복 착용 시 무료!',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Bukchon Hanok, Geheimer Garten & Gwangjang Markt',
        dateFormattedDe: 'Samstag, 12.09.2026 (🛸 Hangang Drohnenshow)',
        dateFormattedEn: 'Saturday, Sep 12, 2026 (🛸 Drone Light Show)',
        dateFormattedKr: '2026.09.12 (토) 🛸 한강 드론라이트쇼',
        dayNameEn: 'Saturday: Bukchon Hanok, Secret Garden & Gwangjang Market',
        dayNameKr: '토요일: 북촌한옥마을 & 후원 & 광장시장',
        themeTitleDe: 'Historische Ziegeldächer, royale Gärten & Street Food',
        themeTitleEn: 'Traditional tiled roofs, royal gardens & street food',
        themeTitleKr: '전통 기와 한옥 & 비밀의 화원 & 전통시장',
        spotIds: ['seoul-bukchon-hanok', 'seoul-changdeokgung-secret-garden', 'seoul-gwangjang-market'],
        schedule: [
          {
            time: '09:30 – 12:00',
            titleDe: 'Bukchon Hanok Dorf & Panoramagassen',
            titleEn: 'Bukchon Hanok Village 8 Views',
            titleKr: '북촌한옥마을 8경',
            spotId: 'seoul-bukchon-hanok',
            tipDe: 'Gahoe-dong 31 Gasse bergab: Wundervoller Kontrast aus alten Ziegeldächern und modernen Wolkenkratzern.',
            tipEn: 'Gahoe-dong 31 alley looking down towards N Seoul Tower.',
            tipKr: '전통 한옥 기와지붕 너머로 N서울타워가 보이는 대표 포토존.',
          },
          {
            time: '13:00 – 15:30',
            titleDe: 'Changdeokgung Palast & Geheimer Garten (Huwon)',
            titleEn: 'Changdeokgung Palace & Secret Garden (Huwon)',
            titleKr: '창덕궁과 후원',
            spotId: 'seoul-changdeokgung-secret-garden',
            tipDe: 'UNESCO-Weltkulturerbe mit idyllischen Seepavillons und jahrhundertealten Baumriesen.',
            tipEn: 'UNESCO World Heritage palace with tranquil nature pavilions.',
            tipKr: '자연과 궁궐 건축이 완벽하게 어우러진 비원(후원) 관람.',
          },
          {
            time: '16:30 – 19:30',
            titleDe: 'Gwangjang Markt – Legendäres Food-Paradies',
            titleEn: 'Gwangjang Market Food Feast',
            titleKr: '광장시장 먹거리 투어',
            spotId: 'seoul-gwangjang-market',
            tipDe: 'Setz dich direkt an die Theken: Knusprige Bindaetteok + Mayak Kimbap + Kalguksu. Niemand stört sich an Einzelpersonen!',
            tipEn: 'Take a seat at open stalls: Crispy mung bean pancakes and fresh kalguksu noodles.',
            tipKr: '녹두빈대떡, 마약김밥, 손칼국수를 포장마차 카운터에서 편하게 혼밥!',
            isSoloFood: true,
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Inwangsan Festungsmauer-Wanderung',
        dateFormattedDe: 'Sonntag, 13.09.2026',
        dateFormattedEn: 'Sunday, Sep 13, 2026',
        dateFormattedKr: '2026.09.13 (일)',
        dayNameEn: 'Sunday: Inwangsan Mountain Fortress Wall Hike',
        dayNameKr: '일요일: 인왕산 한양도성길 등산',
        themeTitleDe: 'Kurze Bergwanderung mit Skyline-Blick',
        themeTitleEn: 'Scenic ridge hike with skyline panoramas',
        themeTitleKr: '도심 전경이 내려다보이는 성곽길 트레킹',
        spotIds: ['seoul-inwangsan-trail'],
        schedule: [
          {
            time: '15:00 – 18:30',
            titleDe: 'Inwangsan Bergkamm & Stadtmauer',
            titleEn: 'Inwangsan Fortress Wall Trail',
            titleKr: '인왕산 한양도성 성곽길',
            spotId: 'seoul-inwangsan-trail',
            tipDe: '1-stündiger Aufstieg auf Treppen entlang der Stadtmauer. Spektakulärer Sonnenuntergang über Seoul.',
            tipEn: '1-hour hike along illuminated stone walls with 360-degree city views.',
            tipKr: '해질녘 성곽을 따라 걷는 서울 최고의 노을 & 야경 트레킹 코스.',
          },
        ],
      },
    },
  },
  {
    id: 'weekend-3-incheon-coast',
    titleDe: 'Wochenende 3: Incheon Meeresbrise, Chinatown & Wolmido Island',
    dateRange: '18.09. – 20.09.2026',
    titleEn: 'Weekend 3: Incheon Chinatown, Ocean Breeze & Wolmido Island',
    titleKr: '주말 3: 인천 차이나타운 & 월미도 & 개항장',
    month: 'sep',
    badgeDe: '🌊 Meeresluft & Vintage',
    badgeEn: '🌊 Ocean & Vintage',
    badgeKr: '🌊 바다 & 개항장',
    badgeType: 'coast',
    region: 'Incheon',
    descriptionDe: 'Koreas einziges historisches Chinatown (seit 1884), 3D-Märchenviertel, Wolmi Sea Train & Sonnenuntergang über dem Westmeer.',
    descriptionEn: "Korea's only historic Chinatown, 3D fairytale village, ocean monorail & West Sea sunset.",
    descriptionKr: '짜장면의 발상지 차이나타운, 송월동 동화마을, 월미바다열차와 서해 일몰.',
    soloHighlightDe: 'Solo-Food: Echtes traditionelles Jjajangmyeon (schwarze Bohnennudeln) – das ultimative koreanische Solo-Gericht.',
    soloHighlightEn: 'Solo Food: Original Jjajangmyeon black bean noodles in Chinatown – the iconic Korean solo comfort dish.',
    soloHighlightKr: '혼밥 추천: 차이나타운 원조 짜장면 & 공갈빵 간식.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Chinatown, Märchenbezirk & Retro-Klinik Café',
        dateFormattedDe: 'Freitag, 18.09.2026',
        dateFormattedEn: 'Friday, Sep 18, 2026',
        dateFormattedKr: '2026.09.18 (금)',
        dayNameEn: 'Friday: Chinatown, Fairy Tale Village & Retro Clinic Cafe',
        dayNameKr: '금요일: 차이나타운 & 동화마을 & 브라운핸즈',
        themeTitleDe: 'Chinesische Kultur, bunte Wandbilder & 19. Jhdt. Kolonialbauten',
        themeTitleEn: 'Chinese heritage, vibrant murals & 19th-century colonial streets',
        themeTitleKr: '붉은 패루 & 3D 벽화 & 근대 역사 거리',
        spotIds: ['incheon-chinatown', 'incheon-songwoldong-fairytale', 'incheon-gaehangro-brownhands'],
        schedule: [
          {
            time: '11:30 – 13:30',
            titleDe: 'Incheon Chinatown & Jjajangmyeon Lunch',
            titleEn: 'Incheon Chinatown & Jjajangmyeon Lunch',
            titleKr: '인천 차이나타운 & 짜장면 점심',
            spotId: 'incheon-chinatown',
            tipDe: 'Geburtsort von Jjajangmyeon! Bestelle dir eine Schüssel im historischen Restaurant Gonghwachun.',
            tipEn: 'Birthplace of Jjajangmyeon! Enjoy an authentic bowl at Gonghwachun.',
            tipKr: '차이나타운 원조 맛집에서 짜장면 한 그릇으로 맛있는 점심!',
            isSoloFood: true,
          },
          {
            time: '13:30 – 15:00',
            titleDe: 'Songwol-dong Märchenbezirk & Regenbogentreppen',
            titleEn: 'Songwol-dong Fairy Tale Village',
            titleKr: '송월동 동화마을',
            spotId: 'incheon-songwoldong-fairytale',
            tipDe: 'Farbenfrohes Viertel mit Pinocchio-Wänden und bunten Treppen direkt neben Chinatown.',
            tipEn: 'Whimsical neighborhood transformed into a storybook world with 3D street art.',
            tipKr: '아기자기한 벽화와 무지개 계단이 있는 동화 같은 골목길 산책.',
          },
          {
            time: '15:30 – 18:00',
            titleDe: 'Gaehang-ro Retro-Klinik & Brown Hands Café',
            titleEn: 'Brown Hands Heritage Clinic Cafe',
            titleKr: '브라운핸즈 개항로 카페',
            spotId: 'incheon-gaehangro-brownhands',
            tipDe: 'Ehemalige HNO-Klinik aus den 1960ern, umgewandelt in ein 4-stöckiges Backstein-Café mit herrlichem Kaffee.',
            tipEn: '1960s former ENT hospital clinic turned into a 4-story vintage brick coffee haven.',
            tipKr: '1960년대 옛 이비인후과 건물을 재생한 감성 넘치는 4층 빈티지 카페.',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Wolmido Insel, Wolmi Sea Train & Sonnenuntergang',
        dayNameEn: 'Saturday: Wolmido Island, Sea Train & Ocean Sunset',
        dayNameKr: '토요일: 월미도 바다열차 & 테마파크 & 일몰',
        themeTitleDe: 'Meerespromenade, Hochbahn über dem Wasser & Glas-Aussichtsturm',
        themeTitleEn: 'Seaside boardwalk, monorail over ocean & glass tower',
        themeTitleKr: '서해 바닷바람 & 모노레일 & 월미산 전망대',
        spotIds: ['incheon-wolmido-theme-park', 'incheon-wolmi-observatory-tower'],
        schedule: [
          {
            time: '13:00 – 15:30',
            titleDe: 'Wolmi Sea Train (Panoramabahn)',
            titleEn: 'Wolmi Sea Train Monorail',
            titleKr: '월미바다열차 탑승',
            tipDe: 'Fahre mit der Hochbahn einmal rund um die Insel mit freiem Blick auf Hafenkräne und das offene Meer.',
            tipEn: 'Ride Korea’s longest urban monorail circling the island with ocean views.',
            tipKr: '국내 최장 도심형 관광 모노레일을 타고 월미도 한 바퀴 둘러보기.',
          },
          {
            time: '15:30 – 17:30',
            titleDe: 'Wolmido Promenade & Meeressteg',
            titleEn: 'Wolmido Culture Street & Boardwalk',
            titleKr: '월미도 문화의 거리 & 등대길',
            spotId: 'incheon-wolmido-theme-park',
            tipDe: 'Möwen füttern, Seeluft atmen und das kultige "Disco Pang Pang" Fahrgeschäft beobachten.',
            tipEn: 'Seaside promenade with ocean Ferris wheel and famous Disco Pang Pang ride.',
            tipKr: '바닷가 데크길 산책과 월미테마파크 디스코팡팡 구경.',
          },
          {
            time: '17:30 – 19:30',
            titleDe: 'Wolmisan Glas-Aussichtsturm (100% Kostenlos)',
            titleEn: 'Wolmi Mountain Glass Observatory Tower',
            titleKr: '월미산 전망대 360도 일몰',
            spotId: 'incheon-wolmi-observatory-tower',
            tipDe: '25 Meter hoher Glasturm auf dem Berg. Spektakulärer Sonnenuntergang über der 21 km Incheon-Brücke!',
            tipEn: '25m glass cylinder tower atop Wolmi mountain with 360° views over Incheon Bridge.',
            tipKr: '무료 엘리베이터를 타고 올라가 인천대교 너머로 떨어지는 붉은 노을 감상.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Bupyeong Modoo Mall (Guinness-Rekord Untergrundmarkt)',
        dayNameEn: 'Sunday: Bupyeong Modoo Mall (World’s Largest Underground Mall)',
        dayNameKr: '일요일: 부평모두몰 (세계 최대 지하상가)',
        themeTitleDe: '1.400 Läden für K-Fashion, Streetwear & Snacks',
        themeTitleEn: '1,400 shops for affordable K-fashion & streetwear',
        themeTitleKr: '1,400개 매장의 기네스북 등재 지하 쇼핑 천국',
        spotIds: ['incheon-bupyeong-underground-market'],
        schedule: [
          {
            time: '13:00 – 17:00',
            titleDe: 'Bupyeong Modoo Mall Shopping & Street Snacks',
            titleEn: 'Bupyeong Modoo Mall Bargain Hunt',
            titleKr: '부평모두몰 쇼핑 & 분식',
            spotId: 'incheon-bupyeong-underground-market',
            tipDe: 'Größtes unterirdisches Einkaufszentrum der Welt. Perfekt zum Shoppen von K-Fashion zu unschlagbaren Preisen.',
            tipEn: 'Guinness World Record underground mall packed with affordable fashion.',
            tipKr: '비 오는 날에도 걱정 없이 즐기는 가성비 최고 K-패션 지하상가.',
            isSoloFood: true,
          },
        ],
      },
    },
  },
  {
    id: 'weekend-4-suwon-media-art',
    titleDe: 'Wochenende 4: UNESCO-Festung Suwon & Media Art 3D-Lichtfestival',
    dateRange: '25.09. – 27.09.2026',
    titleEn: 'Weekend 4: UNESCO Fortress Suwon & Media Art 3D Light Show',
    titleKr: '주말 4: 수원화성 & 미디어아트 축제 & 행궁동',
    month: 'sep',
    badgeDe: '🏯 UNESCO 3D-Lichtshow',
    badgeEn: '🏯 UNESCO 3D Light Show',
    badgeKr: '🏯 수원화성 미디어아트',
    badgeType: 'event',
    region: 'Gyeonggi',
    descriptionDe: 'Monumentale Joseon-Festungsmauern werden zur 3D-Projektionsleinwand, Heißluftballon Flying Suwon & gemütliche K-Drama-Cafégassen.',
    descriptionEn: 'Historic stone fortress walls turned into colossal 3D projection mappings, tethered hot air balloon & cozy K-drama cafes.',
    descriptionKr: '성벽을 화려하게 물들이는 3D 미디어아트와 플라잉수원 열기구, 행궁동 카페거리.',
    soloHighlightDe: 'Solo-Aktivität: Festungsmauer komplett alleine ablaufen – extrem sicher, weitläufig und meditativ.',
    soloHighlightEn: 'Solo Activity: Walking the 5.7km fortress ramparts alone is deeply relaxing and scenic.',
    soloHighlightKr: '혼행 매력: 탁 트인 성곽길을 따라 조용히 걸으며 사색하기 좋은 힐링 코스.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Anreise, Heißluftballon & Haenggung-dong Cafés',
        dateFormattedDe: 'Freitag, 25.09.2026',
        dateFormattedEn: 'Friday, Sep 25, 2026',
        dateFormattedKr: '2026.09.25 (금)',
        dayNameEn: 'Friday: Flying Suwon Balloon & K-Drama Cafes',
        dayNameKr: '금요일: 플라잉수원 열기구 & 행궁동 공방거리',
        themeTitleDe: 'Ausblick aus 150m Höhe & charmante Gassen',
        themeTitleEn: '150m aerial view & charming alley boutiques',
        themeTitleKr: '150m 상공 열기구 전망 & 감성 카페',
        spotIds: ['event-suwon-hwaseong-media-art'],
        schedule: [
          {
            time: '14:00 – 16:30',
            titleDe: 'Haenggung-dong Café Street & Boutiquen',
            titleEn: 'Haenggung-dong Cafe Street & Boutiques',
            titleKr: '행궁동 카페거리 & 베이커리',
            tipDe: 'Gassen direkt an den Burgmauern, Drehorte beliebter K-Dramen (Our Beloved Summer).',
            tipEn: 'Charming alleys along fortress walls featured in K-Dramas.',
            tipKr: '성곽을 따라 늘어선 예쁜 루프탑 카페와 공방들.',
            isSoloFood: true,
          },
          {
            time: '17:00 – 18:30',
            titleDe: 'Flying Suwon (Fesselballon 150m Höhe)',
            titleEn: 'Flying Suwon Tethered Helium Balloon',
            titleKr: '플라잉수원 열기구 탑승',
            tipDe: 'Schwebe 150 Meter über Suwon und blicke über die geschwungene Festungsmauer bei Sonnenuntergang.',
            tipEn: 'Rise 150m into the sky for a bird’s eye view of the fortress.',
            tipKr: '150미터 상공에서 내려다보는 수원화성의 완만한 곡선과 석양.',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Festungsmauer-Wanderung & 3D-Media-Art Show',
        dateFormattedDe: 'Samstag, 26.09.2026 (🏯 Suwon 3D Media Art)',
        dateFormattedEn: 'Saturday, Sep 26, 2026 (🏯 Media Art)',
        dateFormattedKr: '2026.09.26 (토) 🏯 수원화성 미디어아트',
        dayNameEn: 'Saturday: Fortress Wall Hike & 3D Media Art Mapping',
        dayNameKr: '토요일: 수원화성 성곽 완주 & 미디어아트 야경',
        themeTitleDe: 'UNESCO-Kulturerbe & monumentale Nachtprojektionen',
        themeTitleEn: 'UNESCO World Heritage & colossal nighttime mappings',
        themeTitleKr: '세계유산 성곽길 & 레이저 미디어쇼',
        spotIds: ['event-suwon-hwaseong-media-art'],
        schedule: [
          {
            time: '13:00 – 17:00',
            titleDe: 'Suwon Hwaseong Festungsmauer Rundweg',
            titleEn: 'Suwon Hwaseong Fortress Rampart Walk',
            titleKr: '수원화성 성곽 완주 코스 (장안문~화서문)',
            tipDe: 'Spaziere von Paldalmun über Hwaseomun zum Janganmun-Tor. Unterwegs Bogenschießen ausprobieren!',
            tipEn: 'Walk the crenellated stone walls past pavilions and secret sluice gates.',
            tipKr: '웅장한 성곽을 따라 걸으며 조선시대 건축의 백미를 감상하세요.',
          },
          {
            time: '17:30 – 19:00',
            titleDe: 'Solo-Dinner: Suwon Wanggalbi / Galbi-Tang',
            titleEn: 'Solo Dinner: Suwon Short Rib Soup (Galbi-Tang)',
            titleKr: '수원 갈비탕 혼밥',
            tipDe: 'Suwon ist weltberühmt für Rippchen. Eine heiße Schüssel Galbi-Tang (Rippchensuppe) ist perfekt für Alleinesser.',
            tipEn: 'Suwon is famed for beef ribs. A steaming bowl of Galbitang soup is ideal for one.',
            tipKr: '수원의 명물 푸짐한 갈비탕 한 뚝배기로 든든하게 저녁 식사.',
            isSoloFood: true,
          },
          {
            time: '19:30 – 21:30',
            titleDe: 'Suwon Hwaseong Media Art Festival (3D-Projektionsshow)',
            titleEn: 'Suwon Hwaseong Media Art Festival (Light Mapping)',
            titleKr: '수원화성 미디어아트 본 공연',
            spotId: 'event-suwon-hwaseong-media-art',
            tipDe: 'Täglich Shows um 19:30, 20:15 und 21:00 Uhr auf den riesigen Steinmauern des Hwaseomun-Tors. Eintritt frei!',
            tipEn: '3 major projection shows nightly on Hwaseomun Gate and Jangan Park lawns. Free admission.',
            tipKr: '화서문 성벽에 펼쳐지는 웅장한 3D 프로젝션 맵핑과 레이저 빛의 향연.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Hwaseong Haenggung Palast & Park',
        dateFormattedDe: 'Sonntag, 27.09.2026',
        dateFormattedEn: 'Sunday, Sep 27, 2026',
        dateFormattedKr: '2026.09.27 (일)',
        dayNameEn: 'Sunday: Hwaseong Haenggung Temporary Palace',
        dayNameKr: '일요일: 화성행궁 & 방화수류정',
        themeTitleDe: 'Königlicher temporärer Palast & Entspannung am Teich',
        themeTitleEn: 'Royal secondary palace & scenic lotus pond',
        themeTitleKr: '왕의 임시 거처 화성행궁과 용연 연못',
        spotIds: ['event-suwon-hwaseong-media-art'],
        schedule: [
          {
            time: '11:00 – 14:00',
            titleDe: 'Banghwasuryujeong Pavillon & Yongyeon-Teich',
            titleEn: 'Banghwasuryujeong Pavilion & Yongyeon Pond',
            titleKr: '방화수류정과 용연 연못 피크닉',
            tipDe: 'Der malerischste Pavillon Koreas, der über einem Weidenteich thront. Picknickmatte ausleihen!',
            tipEn: 'Korea’s most picturesque pavilion perched over a willow-fringed pond.',
            tipKr: '연못 주변 잔디밭에서 돗자리를 깔고 즐기는 한낮의 여유.',
          },
        ],
      },
    },
  },
  {
    id: 'weekend-5-songdo-futuristic',
    titleDe: 'Wochenende 5: Incheon Songdo Smart City – Die Stadt der Zukunft',
    dateRange: '02.10. – 04.10.2026',
    titleEn: 'Weekend 5: Incheon Songdo Smart City of the Future',
    titleKr: '주말 5: 인천 송도 스마트시티 & 센트럴파크',
    month: 'oct',
    badgeDe: '🏙️ Futurismus & Skyline',
    badgeEn: '🏙️ Futurism & Skyline',
    badgeKr: '🏙️ 미래도시 & 야경',
    badgeType: 'coast',
    region: 'Incheon',
    descriptionDe: 'Avantgardistische Wolkenkratzer, Seewasserkanal mit beleuchteten Mondbooten, Tri-Bowl & G-Tower 33F Gratis-Aussicht.',
    descriptionEn: 'Avant-garde skyscrapers, seawater canal with glowing moon boats, Tri-Bowl & G-Tower 33F free sky deck.',
    descriptionKr: '해수 공원 센트럴파크, 문보트 야경, 트라이볼과 G타워 무료 33층 전망대.',
    soloHighlightDe: 'Kostenlos: G-Tower 33. Stockwerk bietet einen unübertroffenen 360-Grad-Blick auf den Park und das Meer.',
    soloHighlightEn: 'Free 33F Sky Deck at G-Tower gives 360° panoramas over canal and ocean bridge.',
    soloHighlightKr: '무료 전망대: G타워 33층 IFEZ 홍보관에서 송도 전체를 한눈에 조망.',
    days: {
      fri: {
        dayNameDe: 'Freitag: G-Tower Sky Deck, Tri-Bowl & Mond-Boote',
        dateFormattedDe: 'Freitag, 02.10.2026 (Palast-Nacht Finissage)',
        dateFormattedEn: 'Friday, Oct 2, 2026',
        dateFormattedKr: '2026.10.02 (금)',
        dayNameEn: 'Friday: G-Tower Observatory, Tri-Bowl & Moon Boats',
        dayNameKr: '금요일: G타워 전망대 & 트라이볼 & 문보트',
        themeTitleDe: 'Smart City Skyline, futuristische Architektur & Seewasserkanal',
        themeTitleEn: 'Smart city skyline, futuristic architecture & canal',
        themeTitleKr: '미래형 건축 & 수상 레저 & 야경',
        spotIds: ['incheon-g-tower-observatory', 'incheon-songdo-central-park'],
        schedule: [
          {
            time: '14:30 – 16:30',
            titleDe: 'G-Tower 33. Stock Gratis Aussichtsplattform',
            titleEn: 'G-Tower 33F Free Sky Observatory',
            titleKr: 'G타워 33층 무료 전망대',
            spotId: 'incheon-g-tower-observatory',
            tipDe: '100% kostenloser Eintritt! Bodentiefe Fenster mit Blick auf den Park und die 21 km lange Incheon Grand Bridge.',
            tipEn: '100% free admission. Floor-to-ceiling glass looking over Central Park and Yellow Sea.',
            tipKr: '엘리베이터를 타고 33층으로 올라가 송도 전경과 서해 바다 감상.',
          },
          {
            time: '16:30 – 18:30',
            titleDe: 'Songdo Central Park & Tri-Bowl Skulptur',
            titleEn: 'Songdo Central Park & Tri-Bowl',
            titleKr: '송도 센트럴파크 & 트라이볼',
            spotId: 'incheon-songdo-central-park',
            tipDe: 'Fotografiere die schwebende Tri-Bowl-Metallstruktur bei Sonnenuntergang und schau dir das Rehe-Gehege an.',
            tipEn: 'Photograph the floating metallic Tri-Bowl curves at golden hour and spot deer.',
            tipKr: '물 위에 떠 있는 곡선형 트라이볼 건축물과 센트럴파크 산책.',
          },
          {
            time: '19:00 – 21:00',
            titleDe: 'Leuchtende Mond-Boote & Skyline-Reflexionen',
            titleEn: 'Illuminated Moon Boats & Canal Reflections',
            titleKr: '문보트 야간 조명 & 카넬워크',
            tipDe: 'Nach Einbruch der Dunkelheit leuchten hunderte bunte LED-Mondboote auf dem Meerwasserkanal.',
            tipEn: 'Watch colorful glowing moon boats glide across the glassy canal under neon skyscrapers.',
            tipKr: '알록달록 불을 밝힌 문보트들이 떠다니는 낭만적인 밤 풍경.',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Yeongjong Meeres-Schienenfahrrad & Cosmo 40',
        dayNameEn: 'Saturday: Yeongjong Ocean Rail Bike & Cosmo 40 Art Center',
        dayNameKr: '토요일: 영종씨사이드 레일바이크 & 코스모40',
        themeTitleDe: 'Radeln an der Meeresbrandung & Industrie-Chic Kulturzentrum',
        themeTitleEn: 'Pedaling by ocean waves & industrial heritage art center',
        themeTitleKr: '바다 옆 레일바이크 & 폐공장 복합문화공간',
        spotIds: ['incheon-yeongjongdo-railbike', 'incheon-cosmo-40'],
        schedule: [
          {
            time: '13:00 – 15:30',
            titleDe: 'Yeongjong Seaside Park Küsten-Schienenfahrrad',
            titleEn: 'Yeongjong Ocean Rail Bike',
            titleKr: '영종씨사이드파크 레일바이크',
            spotId: 'incheon-yeongjongdo-railbike',
            tipDe: '5,6 km langes Schienenfahrrad direkt an der Brandung mit Blick auf die Incheon-Brücke und Möwen.',
            tipEn: 'Pedal 5.6km right along the ocean coastline facing Incheon Grand Bridge.',
            tipKr: '시원한 서해 바닷바람을 맞으며 달리는 5.6km 해안 레일바이크.',
          },
          {
            time: '16:30 – 19:30',
            titleDe: 'Cosmo 40 Kulturzentrum (Alte Chemiefabrik)',
            titleEn: 'Cosmo 40 Industrial Factory Cultural Center',
            titleKr: '코스모40 복합문화공간',
            spotId: 'incheon-cosmo-40',
            tipDe: 'Stillgelegte Fabrik der 1970er Jahre mit erhaltenen Kränen, Rohrsystemen, Kunstgalerien und Craft-Pizza.',
            tipEn: 'Preserved 1970s chemical plant turned into an art center and bakery cafe.',
            tipKr: '거대한 공장 크레인과 파이프가 남아있는 독특한 힙 플레이스 카페.',
            isSoloFood: true,
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Songdo Triple Street & Regenschirm-Promenade',
        dateFormattedDe: 'Sonntag, 04.10.2026',
        dateFormattedEn: 'Sunday, Oct 4, 2026',
        dateFormattedKr: '2026.10.04 (일)',
        dayNameEn: 'Sunday: Songdo Triple Street & Umbrella Skyway',
        dayNameKr: '일요일: 송도 트리플스트리트 & 현대프리미엄아울렛',
        themeTitleDe: 'Bunte Regenschirm-Überdachung, Gourmet-Passagen & Dachgarten',
        themeTitleEn: 'Colorful umbrella canopy, gourmet avenues & rooftop park',
        themeTitleKr: '무지개빛 우산 하늘거리 & 루프탑 공원 산책',
        spotIds: ['incheon-songdo-triple-street'],
        schedule: [
          {
            time: '13:00 – 16:30',
            titleDe: 'Songdo Triple Street & Schwebende Regenschirme',
            titleEn: 'Songdo Triple Street & Umbrella Skyway',
            titleKr: '송도 트리플스트리트 & 현대아울렛',
            spotId: 'incheon-songdo-triple-street',
            tipDe: 'Berühmt für die bunten Regenschirme über der Flaniermeile, weitläufige Foodcourts und den Panoramablick von den Dachgärten.',
            tipEn: 'Famous for its vibrant suspended umbrella canopy, extensive food avenues, and rooftop skyline gardens.',
            tipKr: '화려한 우산 하늘길 아래에서 사진을 남기고, 지하 맛집 거리와 옥상 정원을 여유롭게 즐기기.',
            isSoloFood: true,
          },
        ],
      },
    },
  },
  {
    id: 'weekend-6-silver-grass',
    titleDe: 'Wochenende 6: Haneul Silbergras-Festival & Hongdae Indie-Kultur',
    dateRange: '16.10. – 18.10.2026',
    titleEn: 'Weekend 6: Haneul Silver Grass Festival & Hongdae Indie Scene',
    titleKr: '주말 6: 하늘공원 억새축제 & 홍대 인디 문화',
    month: 'oct',
    badgeDe: '🌾 Silbergras-Festival (Okt)',
    badgeEn: '🌾 Silver Grass Fest (Oct)',
    badgeKr: '🌾 억새축제 & 버스킹',
    badgeType: 'foliage',
    region: 'Seoul',
    descriptionDe: 'Wogende weiße Silbergrasfelder auf dem Haneul-Plateau bei Sonnenuntergang, beleuchtete Nachtpfade & K-Pop Busking in Hongdae.',
    descriptionEn: 'Waves of silver eulalia grass bathed in golden sunset atop Haneul Park plateau, night illumination & Hongdae busking.',
    descriptionKr: '하늘공원 은빛 억새밭의 장관과 해질녘 한강 노을, 홍대 거리 버스킹.',
    soloHighlightDe: 'Fotoparadies für Solo: Holzstege durch meterhohes Silbergras – atemberaubende Selfies & Panoramabilder.',
    soloHighlightEn: 'Solo Photography: Walk the wooden paths through tall silver grass overlooking the river.',
    soloHighlightKr: '혼행 꿀팁: 억새밭 사이 데크길을 걸으며 인생샷과 한강 일몰 담기.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Haneul Silbergras-Festival bei Sonnenuntergang',
        dateFormattedDe: 'Freitag, 16.10.2026',
        dateFormattedEn: 'Friday, Oct 16, 2026',
        dateFormattedKr: '2026.10.16 (금)',
        dayNameEn: 'Friday: Haneul Silver Grass Festival at Sunset',
        dayNameKr: '금요일: 하늘공원 억새축제 & 노을',
        themeTitleDe: 'Wogende Pampasgras-Felder, Han-Fluss-Blick & Lichterpfad',
        themeTitleEn: 'Waving silver grass fields, river vistas & light trail',
        themeTitleKr: '황금빛 억새밭과 야간 조명 산책로',
        spotIds: ['seoul-hongdae-street'],
        schedule: [
          {
            time: '15:30 – 18:30',
            titleDe: 'Haneul Park (하늘공원) Silbergras-Festival',
            titleEn: 'Haneul Park Silver Grass Festival',
            titleKr: '하늘공원 억새축제',
            tipDe: 'Nimm die 291 Holzstufen oder den Frosch-Elektrobus. Oben erstreckt sich ein endloses Meer aus silbernem Pampasgras!',
            tipEn: 'Climb the 291 sky stairs to witness endless waves of silver grass overlooking the river at golden hour.',
            tipKr: '하늘계단을 올라 탁 트인 고원에서 은빛 억새와 노을을 감상하세요.',
          },
          {
            time: '18:30 – 20:00',
            titleDe: 'Beleuchtete Nachtpfade im Silbergras',
            titleEn: 'Illuminated Night Light Trails',
            titleKr: '억새밭 야간 라이팅 쇼',
            tipDe: 'Während des Oktober-Festivals wird das Silbergras nach Sonnenuntergang in sanften bunten LED-Farben beleuchtet.',
            tipEn: 'During the festival, the fields are bathed in colorful gentle LED lights after dark.',
            tipKr: '축제 기간 밤마다 은은하게 켜지는 조명과 미디어아트.',
          },
          {
            time: '20:30 – 22:30',
            titleDe: 'Mangwon Traditional Market – Solo Street Food',
            titleEn: 'Mangwon Market Solo Bites',
            titleKr: '망원시장 맛집 투어',
            tipDe: 'Nahegelegener Kultmarkt für frittiertes Hühnchen (Dakgangjeong), Hotteok und Krokett-Sandwiches.',
            tipEn: 'Local trendy market famed for crispy dakgangjeong fried chicken and croquettes.',
            tipKr: '망원시장의 유명한 닭강정, 고로케, 칼국수로 맛있는 야식!',
            isSoloFood: true,
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Yeonnam-dong, Hongdae Busking & Hangang Drohnenshow',
        dateFormattedDe: 'Samstag, 17.10.2026 (🌾 Silbergras-Eröffnung)',
        dateFormattedEn: 'Saturday, Oct 17, 2026 (🌾 Silver Grass Fest)',
        dateFormattedKr: '2026.10.17 (토) 🌾 하늘공원 억새축제',
        dayNameEn: 'Saturday: Yeonnam Forest, Hongdae Busking & Hangang Drones',
        dayNameKr: '토요일: 연남동 숲길 & 홍대 버스킹 & 드론쇼',
        themeTitleDe: 'Indie-Boutiquen, K-Pop Dance-Battles & 1.000 Drohnen am Fluss',
        themeTitleEn: 'Indie boutiques, K-pop dance battles & 1,000 river drones',
        themeTitleKr: '경의선숲길 산책 & 버스킹 & 뚝섬 드론라이트쇼',
        spotIds: ['seoul-yeonnam-forest-park', 'seoul-hongdae-street', 'event-hangang-drone-light-show'],
        schedule: [
          {
            time: '14:00 – 16:30',
            titleDe: 'Yeonnam-dong Gyeongui Line Forest Park (Yeontral Park)',
            titleEn: 'Yeonnam-dong Gyeongui Line Forest Park',
            titleKr: '연남동 경의선숲길',
            spotId: 'seoul-yeonnam-forest-park',
            tipDe: 'Begrünte stillgelegte Bahntrasse mit handwerklichen Bäckereien und charmanten Boutiquen.',
            tipEn: 'Linear forest park along former railway lines lined with boutique cafes.',
            tipKr: '기찻길을 따라 조성된 도심 속 녹지 공간과 골목 카페들.',
          },
          {
            time: '16:30 – 18:30',
            titleDe: 'Hongdae Busking & Street Performance',
            titleEn: 'Hongdae Youth Busking Street',
            titleKr: '홍대 걷고싶은거리 버스킹',
            spotId: 'seoul-hongdae-street',
            tipDe: 'Live K-Pop Tanzgruppen, Straßenmusiker und Fotokabinen (Life Four Cuts).',
            tipEn: 'Live K-pop dance battles and indie musicians performing on the street.',
            tipKr: '열정 넘치는 길거리 버스킹 공연과 인생네컷 사진 찍기.',
          },
          {
            time: '19:30 – 20:30',
            titleDe: 'Hangang Drohnen-Lichtshow (Ttukseom Hangang Park)',
            titleEn: 'Hangang Drone Light Show (Ttukseom)',
            titleKr: '한강 드론 라이트쇼 (뚝섬한강공원)',
            spotId: 'event-hangang-drone-light-show',
            tipDe: 'Jeden Samstagabend um 20:00 Uhr: 1.000 Drohnen zeichnen 3D-Bilder in den Nachthimmel über dem Fluss. 100% kostenlos!',
            tipEn: '1,000 LED drones forming 3D constellations directly above Han River. Completely free!',
            tipKr: '1,000대 이상의 드론이 한강 밤하늘을 수놓는 무료 드론 라이트쇼.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Seoul Forest & Seongsu-dong Industrie-Cafés',
        dayNameEn: 'Sunday: Seoul Forest & Seongsu Industrial Cafes',
        dayNameKr: '일요일: 서울숲 단풍 & 성수동 카페거리',
        themeTitleDe: 'Ginkgo-Alleen, Rehe füttern & Industriehallen-Charme',
        themeTitleEn: 'Ginkgo corridors, deer corral & industrial chic cafes',
        themeTitleKr: '서울숲 은행나무길 & 성수동 붉은 벽돌 카페',
        spotIds: ['seoul-seoul-forest', 'seoul-seongsu-cafe-street'],
        schedule: [
          {
            time: '12:00 – 16:00',
            titleDe: 'Seoul Forest Ginkgo-Wälder & Cafe Onion Seongsu',
            titleEn: 'Seoul Forest Autumn Walk & Cafe Onion',
            titleKr: '서울숲 산책 & 카페 어니언 성수',
            spotId: 'seoul-seoul-forest',
            tipDe: 'Goldgelbes Herbstlaub im Seoul Forest, danach leckere Pandoro-Backwaren im Cafe Onion Seongsu.',
            tipEn: 'Vibrant autumn colors in the park, then artisan pastries at Cafe Onion in converted factory.',
            tipKr: '황금빛 은행나무 숲길 산책 후 폐공장을 개조한 성수동 핫플 카페 방문.',
            isSoloFood: true,
          },
        ],
      },
    },
  },
  {
    id: 'weekend-7-autumn-foliage',
    titleDe: 'Wochenende 7: Peak Herbstlaub (Danpung / 단풍) in Seouls Palästen',
    dateRange: '06.11. – 08.11.2026',
    titleEn: 'Weekend 7: Peak Autumn Foliage (Danpung) in Royal Palaces',
    titleKr: '주말 7: 고궁의 단풍 절정 (단풍놀이) & 남산',
    month: 'nov',
    badgeDe: '🍁 Peak Herbstlaub (Danpung)',
    badgeEn: '🍁 Peak Foliage (Danpung)',
    badgeKr: '🍁 궁궐 단풍 절정',
    badgeType: 'foliage',
    region: 'Seoul',
    descriptionDe: 'Die schönste Woche des Jahres in Korea: Feurig rote Ahorne im Changdeokgung Secret Garden, Deoksugung Steinmauerweg & N Seoul Tower Herbstwald.',
    descriptionEn: 'The most photogenic week in Korea: Fiery red maples in Changdeokgung Secret Garden, Deoksugung stonewall & Namsan forest.',
    descriptionKr: '창덕궁 후원의 단풍 절경, 덕수궁 돌담길, 남산 순환로 단풍 터널.',
    soloHighlightDe: 'Solo-Spaziergang: Der Deoksugung Doldam-gil (Steinmauerweg) und Namsan-Hügel sind Seouls beliebteste Entspannungswege.',
    soloHighlightEn: 'Solo Walk: Strolling along Deoksugung curved stone wall walkway under golden ginkgo canopies.',
    soloHighlightKr: '혼행 산책: 노란 은행잎이 흩날리는 덕수궁 돌담길과 정동전망대 감상.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Deoksugung Steinmauerweg & Jeongdong Gratis-Aussicht',
        dateFormattedDe: 'Freitag, 06.11.2026 (🍁 Peak Herbstlaub)',
        dateFormattedEn: 'Friday, Nov 6, 2026 (🍁 Peak Foliage)',
        dateFormattedKr: '2026.11.06 (금) 🍁 고궁 단풍 절정',
        dayNameEn: 'Friday: Deoksugung Stonewall & Jeongdong Free View',
        dayNameKr: '금요일: 덕수궁 돌담길 & 정동전망대 13층',
        themeTitleDe: 'Goldene Ginkgo-Alleen & Blick von oben auf die Palastdächer',
        themeTitleEn: 'Golden ginkgo avenues & aerial palace roof view',
        themeTitleKr: '노란 은행나무 터널 & 고궁 조망',
        spotIds: ['seoul-deoksugung-stonewall', 'seoul-jeongdong-observatory'],
        schedule: [
          {
            time: '14:00 – 15:30',
            titleDe: 'Jeong-dong Aussichtsplattform 13. Stock (100% Kostenlos)',
            titleEn: 'Jeong-dong Observatory 13F (Free Deck)',
            titleKr: '정동전망대 13층 (무료 전망)',
            spotId: 'seoul-jeongdong-observatory',
            tipDe: 'Im 13. Stock des Rathauses Seosomun: Fantastischer Blick senkrecht hinab auf die Palastdächer im Herbstlaub!',
            tipEn: 'Hidden free observation lounge in City Hall annex overlooking Deoksugung Palace foliage.',
            tipKr: '단풍으로 물든 덕수궁의 기와지붕이 한눈에 내려다보이는 무료 전망 쉼터.',
          },
          {
            time: '15:30 – 17:30',
            titleDe: 'Deoksugung Palast & Steinmauerweg (Doldam-gil)',
            titleEn: 'Deoksugung Palace & Stonewall Walkway',
            titleKr: '덕수궁 & 덕수궁 돌담길 산책',
            spotId: 'seoul-deoksugung-stonewall',
            tipDe: 'Romantischer Weg entlang der alten Mauer unter einem dichten Dach aus goldenen Ginkgoblättern.',
            tipEn: 'Romantic curved stone wall walkway shaded by massive golden ginkgo trees.',
            tipKr: '가을 정취가 가득한 고즈넉한 돌담길을 따라 여유롭게 걸어보세요.',
          },
          {
            time: '18:00 – 19:30',
            titleDe: 'Solo-Dinner: Mugyo-dong Bugeotguk (Trockenfischsuppe)',
            titleEn: 'Solo Dinner: Mugyo-dong Dried Pollack Soup',
            titleKr: '무교동 북어국집 혼밥',
            tipDe: 'Legendäres Solo-Lokal nahe City Hall. Nur ein einziges Gericht, ultraschnell, wärmend und köstlich.',
            tipEn: 'Legendary single-dish soul food soup spot near City Hall. Welcomes solo eaters.',
            tipKr: '시청 근처 든든하고 깊은 맛의 북어국 단일 메뉴 노포.',
            isSoloFood: true,
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Secret Garden Farbenpracht & N Seoul Tower Herbstwald',
        dateFormattedDe: 'Samstag, 07.11.2026',
        dateFormattedEn: 'Saturday, Nov 7, 2026',
        dateFormattedKr: '2026.11.07 (토)',
        dayNameEn: 'Saturday: Secret Garden Autumn Colors & N Seoul Tower Forest',
        dayNameKr: '토요일: 창덕궁 후원 단풍 절경 & 남산 N서울타워',
        themeTitleDe: 'Königlich feuerrotes Ahornlaub & 360-Grad-Lichtermeer',
        themeTitleEn: 'Royal crimson maples & 360-degree night skyline',
        themeTitleKr: '비원의 붉은 단풍 & 남산타워 파노라마 야경',
        spotIds: ['seoul-changdeokgung-secret-garden', 'seoul-n-seoul-tower'],
        schedule: [
          {
            time: '10:30 – 13:00',
            titleDe: 'Changdeokgung Secret Garden (Huwon) – Danpung Highlight',
            titleEn: 'Changdeokgung Secret Garden Autumn Peak',
            titleKr: '창덕궁 후원 단풍 절정 관람',
            spotId: 'seoul-changdeokgung-secret-garden',
            tipDe: 'Der Buyeongji-Teich umrahmt von feuerrotem Ahorn. Die berühmteste Herbstkulisse Koreas!',
            tipEn: 'Buyeongji lotus pond flanked by fiery red maple trees. Korea’s iconic autumn postcard.',
            tipKr: '부용지와 주합루 주변을 붉게 물들인 조선 왕실의 정원.',
          },
          {
            time: '14:30 – 17:00',
            titleDe: 'Namsan Park Rundweg durch den Herbstwald',
            titleEn: 'Namsan Mountain Circular Autumn Trail',
            titleKr: '남산 둘레길 단풍 터널 걷기',
            tipDe: 'Spaziere von Myeongdong durch den bunten Blätterwald hinauf zum Namsan-Gipfel.',
            tipEn: 'Scenic uphill walk through vibrant deciduous forest towards the summit.',
            tipKr: '노란 은행잎과 붉은 단풍이 어우러진 남산 순환 산책로.',
          },
          {
            time: '17:30 – 20:00',
            titleDe: 'N Seoul Tower (Namsan) – Sonnenuntergang bis Nacht',
            titleEn: 'N Seoul Tower Sunset & Night Panorama',
            titleKr: 'N서울타워 일몰 & 서울 전경 야경',
            spotId: 'seoul-n-seoul-tower',
            tipDe: '360-Grad-Rundumblick über das abendliche Lichtermeer von Seoul und die Han-Fluss-Brücken.',
            tipEn: 'Iconic tower offering sweeping vistas of Seoul glowing under dark autumn skies.',
            tipKr: '해 질 무렵부터 서울 도심의 빌딩 숲이 불을 밝히는 파노라마 야경.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Eunpyeong Hanok Dorf vor Bukhansan Gipfeln',
        dayNameEn: 'Sunday: Eunpyeong Hanok Village & Bukhansan Peaks',
        dayNameKr: '일요일: 은평한옥마을 & 북한산 가을 풍경',
        themeTitleDe: 'Ruhiges Hanok-Dorf, Café mit Bergblick & Teehaus',
        themeTitleEn: 'Serene Hanok enclave, mountain terrace cafe & tea house',
        themeTitleKr: '북한산 아래 고즈넉한 한옥마을과 1인1잔 카페',
        spotIds: ['seoul-eunpyeong-hanok'],
        schedule: [
          {
            time: '12:00 – 15:30',
            titleDe: 'Eunpyeong Hanok Dorf & Rooftop Café "1 In 1 Jan"',
            titleEn: 'Eunpyeong Hanok Village & "1 In 1 Jan" Rooftop',
            titleKr: '은평한옥마을 & 1인1잔 카페',
            spotId: 'seoul-eunpyeong-hanok',
            tipDe: 'Blick vom 5. Stock über traditionelle Ziegeldächer auf die herbstlich gefärbten Granitfelsen des Bukhansan.',
            tipEn: 'Shoot from 5th-floor glass gallery looking down at Hanoks backed by dramatic granite peaks.',
            tipKr: '전통 차와 디저트를 즐기며 북한산 웅장한 가을 봉우리를 조망.',
            isSoloFood: true,
          },
        ],
      },
    },
  },
  {
    id: 'weekend-8-lantern-lights',
    titleDe: 'Wochenende 8: Seoul Lantern Festival & Cheonggyecheon Lichterparade',
    dateRange: '27.11. – 29.11.2026',
    titleEn: 'Weekend 8: Seoul Lantern Festival & Cheonggyecheon Lights',
    titleKr: '주말 8: 서울 빛초롱축제 & 청계천 등불',
    month: 'nov',
    badgeDe: '🏮 Laternen-Festival',
    badgeEn: '🏮 Lantern Festival',
    badgeKr: '🏮 서울 빛초롱축제',
    badgeType: 'event',
    region: 'Seoul',
    descriptionDe: 'Hunderte handgefertigte Großlaternen schwimmen auf dem Stadtkanal, Insadong Galeriegassen & Ikseon-dong Hanok-Teehäuser.',
    descriptionEn: 'Hundreds of handcrafted lanterns floating on Cheonggyecheon canal, Insadong antique street & Ikseon-dong tea courtyards.',
    descriptionKr: '청계천 물길 위에 떠오르는 전통 등불과 인사동 골목, 익선동 한옥 찻집.',
    soloHighlightDe: 'Kostenloses Flanieren: Der Cheonggyecheon-Bach ist komplett beleuchtet und hat sichere, ebene Gehwege.',
    soloHighlightEn: 'Free Evening Stroll: Cheonggyecheon has safe stone walkways right along the floating lanterns.',
    soloHighlightKr: '혼자 걷기: 물길을 따라 놓인 징검다리를 건너며 등불 전시 감상.',
    days: {
      fri: {
        dayNameDe: 'Freitag: Insadong, Ikseon-dong Hanok-Gassen & Laternenfest',
        dateFormattedDe: 'Freitag, 27.11.2026',
        dateFormattedEn: 'Friday, Nov 27, 2026',
        dateFormattedKr: '2026.11.27 (금)',
        dayNameEn: 'Friday: Insadong Antiques, Ikseon-dong & Lantern Fest',
        dayNameKr: '금요일: 인사동 & 익선동 한옥거리 & 청계천 등불',
        themeTitleDe: 'Traditionelle Teehäuser, Hanok-Cafés & beleuchteter Stadtkanal',
        themeTitleEn: 'Traditional tea houses, Hanok cafes & illuminated canal',
        themeTitleKr: '전통 찻집 & 한옥 베이커리 & 청계천 빛초롱',
        spotIds: ['seoul-ikseondong-hanok', 'seoul-cheonggyecheon'],
        schedule: [
          {
            time: '14:30 – 17:00',
            titleDe: 'Ikseon-dong Hanok-Gassen & Bäckerei-Cafés',
            titleEn: 'Ikseon-dong Hanok Aesthetic Alleys',
            titleKr: '익선동 한옥거리 & 디저트 카페',
            spotId: 'seoul-ikseondong-hanok',
            tipDe: 'Verwinkeltes Hanok-Labyrinth aus den 1920ern mit dampfenden Toastbäckereien und Kunsthandwerk.',
            tipEn: 'Charming maze of 1920s Hanoks transformed into boutique bakeries.',
            tipKr: '아기자기한 골목마다 개성 넘치는 한옥 카페와 소품샵 구경.',
          },
          {
            time: '17:30 – 19:00',
            titleDe: 'Solo-Dinner: Insadong Dolsot Bibimbap',
            titleEn: 'Solo Dinner: Insadong Stone Pot Bibimbap',
            titleKr: '인사동 돌솥비빔밥 혼밥',
            tipDe: 'Knisternder Reis im heißen Steintopf mit Bergkräutern und Gochujang. Ideal für Alleinesser.',
            tipEn: 'Sizzling stone bowl rice with mountain herbs. A staple solo dinner.',
            tipKr: '지글지글 고소한 누룽지가 일품인 돌솥비빔밥 1인 식사.',
            isSoloFood: true,
          },
          {
            time: '19:00 – 21:30',
            titleDe: 'Seoul Lantern Festival am Cheonggyecheon',
            titleEn: 'Seoul Lantern Festival on Cheonggyecheon',
            titleKr: '청계천 서울 빛초롱축제',
            spotId: 'seoul-cheonggyecheon',
            tipDe: 'Hunderte leuchtende Figuren aus traditionellem Hanji-Papier erhellen das Wasser. Trittsteine überqueren!',
            tipEn: 'Hundreds of glowing handcrafted paper lantern sculptures illuminate the stream.',
            tipKr: '물길 위에 설치된 대형 한지 등불 조형물과 포토존.',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: Gwanghwamun Platz & Dongdaemun DDP Night',
        dateFormattedDe: 'Samstag, 28.11.2026',
        dateFormattedEn: 'Saturday, Nov 28, 2026',
        dateFormattedKr: '2026.11.28 (토)',
        dayNameEn: 'Saturday: Gwanghwamun Square & Dongdaemun DDP Night',
        dayNameKr: '토요일: 광화문광장 & 동대문 DDP 야간 산책',
        themeTitleDe: 'König Sejong Statue, Buch-Lounge & Zaha Hadid Lichtshow',
        themeTitleEn: 'King Sejong memorial, outdoor library & Zaha Hadid lights',
        themeTitleKr: '세종대왕 동상 & DDP 알루미늄 곡선 야경',
        spotIds: ['seoul-ddp-dongdaemun', 'seoul-cheonggyecheon'],
        schedule: [
          {
            time: '14:00 – 17:00',
            titleDe: 'Gwanghwamun Square & Freiluft-Bibliothek',
            titleEn: 'Gwanghwamun Square & Open Air Library',
            titleKr: '광화문광장 책마당 산책',
            tipDe: 'Setz dich in einen der bunten Sitzsäcke vor der Statue von König Sejong und genieße die Aussicht.',
            tipEn: 'Relax on colorful bean bags on the pedestrian plaza facing the palace gates.',
            tipKr: '넓은 보행광장에서 빈백에 누워 책을 읽고 광화문 경관 감상.',
          },
          {
            time: '18:00 – 21:00',
            titleDe: 'Dongdaemun Design Plaza (DDP) by Zaha Hadid',
            titleEn: 'Dongdaemun Design Plaza (DDP) by Zaha Hadid',
            titleKr: '동대문디자인플라자 (DDP)',
            spotId: 'seoul-ddp-dongdaemun',
            tipDe: 'Futuristische geschwungene Aluminiumpaneele mit Licht-Installationen und weißer Wendeltreppe.',
            tipEn: 'Curving futuristic architecture with glowing pedestrian bridges and night market.',
            tipKr: '우주선 같은 미래지향적 건물 곡선과 계단 포토존.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Dongdaemun Shopping & Heunginjimun Tor',
        dateFormattedDe: 'Sonntag, 29.11.2026',
        dateFormattedEn: 'Sunday, Nov 29, 2026',
        dateFormattedKr: '2026.11.29 (일)',
        dayNameEn: 'Sunday: Dongdaemun Fashion Malls & East Gate',
        dayNameKr: '일요일: 흥인지문 성곽 & 현대시티아울렛',
        themeTitleDe: 'Historisches Osttor & gemütlicher Shopping-Sonntag',
        themeTitleEn: 'Historic East Gate & comfortable mall stroll',
        themeTitleKr: '흥인지문 공원 잔디 언덕 & 현대시티아울렛',
        spotIds: ['seoul-ddp-dongdaemun'],
        schedule: [
          {
            time: '12:00 – 15:30',
            titleDe: 'Heunginjimun Park Grashügel & Hyundai City Outlet',
            titleEn: 'Heunginjimun Park Grass Hill & Outlet',
            titleKr: '흥인지문 공원 성곽 언덕 산책',
            tipDe: 'Vom Grashügel des Heunginjimun Parks hat man einen genialen Blick auf das alte Tor vor den DDP-Wolkenkratzern.',
            tipEn: 'Perch on the grassy knoll of East Gate Park overlooking ancient city walls.',
            tipKr: '완만한 잔디 언덕 성곽길을 따라 걸으며 동대문 일대 조망.',
          },
        ],
      },
    },
  },
  {
    id: 'weekend-9-winter-wonderland',
    titleDe: 'Wochenende 9: Winter-Magie, Shinsegae Mega-Fassade & 555m Seoul Sky',
    dateRange: '11.12. – 13.12.2026',
    titleEn: 'Weekend 9: Winter Lights, Shinsegae Christmas Facade & Seoul Sky (555m)',
    titleKr: '주말 9: 겨울 조명 축제 & 신세계 미디어 파사드 & 서울스카이',
    month: 'dec',
    badgeDe: '❄️ Winterzauber & LED-Fassade',
    badgeEn: '❄️ Winter Lights & Media Facade',
    badgeKr: '❄️ 윈터 페스타 & 롯데타워',
    badgeType: 'winter',
    region: 'Seoul',
    descriptionDe: 'Asiens berühmteste 3D-LED Weihnachtsfassade am Shinsegae Myeongdong, Zaha Hadids DDP Seoul Light & Glasboden-Blick im 555m Lotte World Tower.',
    descriptionEn: "Asia's most famous Christmas LED media show at Shinsegae Myeongdong, DDP Seoul Light & glass deck at 555m Lotte World Tower.",
    descriptionKr: '신세계백화점 크리스마스 미디어 파사드, DDP 서울라이트, 555m 롯데월드타워 서울스카이.',
    soloHighlightDe: 'Budget-Hack: Lotte World Tower SKY31 im 31. Stock ist 100% kostenlos ohne teures Seoul Sky Ticket!',
    soloHighlightEn: 'Budget Hack: Lotte Tower SKY31 on 31F is 100% free with panoramic glass windows.',
    soloHighlightKr: '꿀팁: 롯데월드타워 SKY31 라운지는 무료 방문증으로 31층 전망 감상 가능!',
    days: {
      fri: {
        dayNameDe: 'Freitag: Myeongdong Weihnachtsfassade & Street Food Markt',
        dayNameEn: 'Friday: Myeongdong Holiday Lights & Street Food Alleys',
        dayNameKr: '금요일: 명동 신세계 미디어 파사드 & 길거리 음식',
        themeTitleDe: 'Gigantische LED-Weihnachtsshow & heiße Winter-Snacks',
        themeTitleEn: 'Colossal LED Christmas show & steaming winter snacks',
        themeTitleKr: '화려한 초대형 크리스마스 조명 & 겨울 간식',
        spotIds: ['seoul-cheonggyecheon'],
        schedule: [
          {
            time: '17:00 – 18:30',
            titleDe: 'Myeongdong Street Food Alleys (Gyeran-ppang & Hotteok)',
            titleEn: 'Myeongdong Winter Street Food Alleys',
            titleKr: '명동 길거리 음식 탐방 (계란빵 & 호떡)',
            tipDe: 'Heißes dampfendes Eierbrot (Gyeran-ppang), Zimt-Hotteok und gegrillter Käse am Spieß – der perfekte Winter-Snack für unterwegs.',
            tipEn: 'Steaming egg bread, sweet honey hotteok, and grilled skewers in bustling neon alleys.',
            tipKr: '추운 겨울날 따뜻한 계란빵, 붕어빵, 호떡 등 맛있는 길거리 간식 맛보기.',
            isSoloFood: true,
          },
          {
            time: '18:30 – 19:30',
            titleDe: 'Shinsegae Department Store Mega 3D-LED Weihnachtsfassade',
            titleEn: 'Shinsegae Myeongdong Christmas 3D Media Facade',
            titleKr: '신세계백화점 본점 크리스마스 미디어 파사드',
            tipDe: 'Die berühmteste Weihnachtsshow Asiens! Tausende Menschen versammeln sich gegenüber dem Hauptpostamt, um die zauberhafte Animation zu bestaunen.',
            tipEn: 'Asia’s premier holiday spectacle: 3D animated LED extravaganza across the heritage facade.',
            tipKr: '서울중앙우체국 앞 광장에서 신세계 본점의 웅장한 크리스마스 영상 관람.',
          },
          {
            time: '20:00 – 21:30',
            titleDe: 'Seoul Plaza Eislaufbahn (Eislaufen für 1.000 KRW)',
            titleEn: 'Seoul Plaza Outdoor Ice Skating Rink (1,000 KRW)',
            titleKr: '서울광장 야외 스케이트장 (1,000원)',
            tipDe: 'Vor dem Rathaus entsteht Seouls winterliche Freilufteisbahn. Schlittschuhverleih für nur 1.000 KRW (ca. 70 Cent)!',
            tipEn: 'Skate beneath illuminated government buildings for just 1,000 KRW including skate rental!',
            tipKr: '시청 앞 광장에서 단돈 1,000원으로 스케이트 대여 및 즐기기.',
          },
        ],
      },
      sat: {
        dayNameDe: 'Samstag: COEX Starfield Library & 555m Lotte World Tower',
        dateFormattedDe: 'Samstag, 12.12.2026 (✨ Seoul Light DDP)',
        dateFormattedEn: 'Saturday, Dec 12, 2026 (✨ Seoul Light DDP)',
        dateFormattedKr: '2026.12.12 (토) ✨ DDP 서울라이트',
        dayNameEn: 'Saturday: COEX Starfield Library & 555m Lotte World Tower',
        dayNameKr: '토요일: 별마당 도서관 트리 & 롯데월드타워 서울스카이',
        themeTitleDe: '13m Bücherwand mit Riesen-Weihnachtsbaum & Glasboden über den Wolken',
        themeTitleEn: '13m towering bookshelves with holiday tree & glass sky deck',
        themeTitleKr: '거대 북트리 & 555미터 상공 투명 유리바닥 전망',
        spotIds: ['seoul-starfield-library', 'seoul-bongeunsa-temple', 'seoul-lotte-world-tower', 'seoul-lotte-sky31-lounge'],
        schedule: [
          {
            time: '11:00 – 13:30',
            titleDe: 'Starfield Library (COEX) & Bongeunsa Tempel',
            titleEn: 'Starfield Library (COEX) & Bongeunsa Temple',
            titleKr: '코엑스 별마당 도서관 & 봉은사',
            spotId: 'seoul-starfield-library',
            tipDe: 'Die 13m hohen beleuchteten Bücherwände erhalten im Dezember einen funkelnden Weihnachtsbaum. Danach ruhiger Spaziergang zum 23m Buddha im Bongeunsa-Tempel.',
            tipEn: 'Towering warm bookshelves centered with giant holiday installation, then historic Bongeunsa temple.',
            tipKr: '대형 크리스마스 트리가 세워진 별마당 도서관과 도심 속 사찰 봉은사.',
          },
          {
            time: '14:30 – 17:30',
            titleDe: 'Lotte World Tower & Seoul Sky (555 Meter)',
            titleEn: 'Lotte World Tower & Seoul Sky (555m)',
            titleKr: '롯데월드타워 서울스카이 전망대',
            spotId: 'seoul-lotte-world-tower',
            tipDe: 'Mit dem schnellsten Doppelstock-Aufzug der Welt in 60 Sekunden in den 120. Stock. Glasboden-Plattform Sky Deck mit Tiefblick!',
            tipEn: 'World’s 6th tallest skyscraper. Sky Deck glass floor at 478m looks straight down.',
            tipKr: '478미터 투명 유리바닥 스카이데크에서 발아래 펼쳐지는 아찔한 서울 조망.',
          },
          {
            time: '18:30 – 21:00',
            titleDe: 'Seoul Light DDP (Monumentale 3D-Winter-Medienkunst)',
            titleEn: 'Seoul Light DDP Winter Media Festival',
            titleKr: 'DDP 서울라이트 겨울 미디어아트 축제',
            spotId: 'seoul-ddp-dongdaemun',
            tipDe: 'Zaha Hadids 222 Meter lange futuristische Aluminiumfassade verwandelt sich abends in eine gigantische animierte Lichtleinwand!',
            tipEn: 'Zaha Hadid’s curving metallic facade becomes a 222m-long canvas for 3D winter projections.',
            tipKr: 'DDP 은빛 외벽을 캔버스 삼아 펼쳐지는 초대형 겨울 빛의 향연.',
          },
        ],
      },
      sun: {
        dayNameDe: 'Sonntag: Gwanghwamun Winter Festa & Weihnachtsmarkt',
        dateFormattedDe: 'Sonntag, 13.12.2026 (🎄 Gwanghwamun Wintermarkt)',
        dateFormattedEn: 'Sunday, Dec 13, 2026 (🎄 Christmas Market)',
        dateFormattedKr: '2026.12.13 (일) 🎄 광화문 마켓',
        dayNameEn: 'Sunday: Gwanghwamun Winter Festa & Christmas Village',
        dayNameKr: '일요일: 광화문 윈터페스타 & 마켓',
        themeTitleDe: 'Glühwein, Kunsthandwerk & beleuchtete Skulpturen',
        themeTitleEn: 'Winter market crafts, hot drinks & glowing sculptures',
        themeTitleKr: '광화문 광장 크리스마스 마켓 & 빛의 조각',
        spotIds: ['seoul-cheonggyecheon'],
        schedule: [
          {
            time: '13:00 – 17:00',
            titleDe: 'Gwanghwamun Christmas Market & Hanji-Laternen',
            titleEn: 'Gwanghwamun Christmas Village & Hanji Lights',
            titleKr: '광화문 크리스마스 마켓 & 먹거리',
            tipDe: 'Gemütliche Holzhütten mit Kunsthandwerk, heißen Waffeln, Glühwein und winterlicher Wohlfühlatmosphäre.',
            tipEn: 'Wooden market chalets selling warm snacks and artisanal Korean crafts.',
            tipKr: '따뜻한 온기와 아기자기한 수공예품이 가득한 유럽풍 크리스마스 마켓.',
            isSoloFood: true,
          },
        ],
      },
    },
  },
];

export const WeekendPlannerModal: React.FC<WeekendPlannerModalProps> = ({
  isOpen,
  onClose,
  language,
  userLocation,
  savedSpotIds,
  onImportSpotsToPlanner,
  onSelectSpot,
}) => {
  // ─── Filter States ───
  const [selectedDays, setSelectedDays] = useState<Weekday[]>(['fri', 'sat']);
  const [selectedMonth, setSelectedMonth] = useState<MonthFilter>('all');
  const [selectedRegion, setSelectedRegion] = useState<RegionFilter>('all');
  const [selectedTheme, setSelectedTheme] = useState<ThemeFilter>('all');
  const [soloOnly, setSoloOnly] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('curated');
  const [copiedPlanId, setCopiedPlanId] = useState<string | null>(null);
  const [selectedGeneratorWeekendId, setSelectedGeneratorWeekendId] = useState<string>('weekend-1-fireworks');

  const t = {
    de: {
      modalTitle: 'Wochenend- & Festival-Planer (Sept – Dez)',
      modalSubtitle: 'Plane deine Freitage, Samstage & Sonntage in Seoul, Incheon & Gyeonggi',
      tabCurated: 'Kuratierte Wochenenden (9 Touren)',
      tabGenerator: 'Dynamischer Tour-Generator',
      tabGuide: 'Solo & Festival Guide',
      filterDaysTitle: 'Deine freien Tage:',
      dayFri: 'Freitag (Fr)',
      daySat: 'Samstag (Sa)',
      daySun: 'Sonntag (So)',
      quickWeekendFrSa: 'Fr + Sa (Standard)',
      quickWeekendFull: 'Fr + Sa + So (Voll)',
      filterMonthTitle: 'Monat / Saison:',
      monthAll: 'Alle (Sept–Dez)',
      monthSep: 'September 2026',
      monthOct: 'Oktober 2026',
      monthNov: 'November 2026',
      monthDec: 'Dezember 2026',
      filterRegionTitle: 'Region:',
      regionAll: 'Alle Regionen',
      regionSeoul: 'Seoul',
      regionIncheon: 'Incheon',
      regionGyeonggi: 'Gyeonggi (Suwon/Goyang)',
      filterThemeTitle: 'Fokus:',
      themeAll: 'Alle Themen',
      themeFestivals: '🎆 Festivals & Drohnenshows',
      themePalaces: '🏯 Paläste & Hanok',
      themeModern: '🏙️ Skyline & Moderne',
      themeNature: '🍁 Natur & Herbstlaub',
      themeFood: '🍲 Solo-Food & Street Food',
      soloToggleLabel: '100% Solo-Friendly Modus (혼놀 / 혼밥)',
      soloToggleSub: 'Empfiehlt nur Orte & Essenslokale ohne 2-Personen-Mindestbestellungen',
      importToPlannerBtn: '⚡ In Routenplaner übernehmen',
      openNaverRoute: 'In Naver Map öffnen',
      openKakaoRoute: 'In KakaoMap öffnen',
      copyPlanBtn: 'Tagesplan kopieren',
      planCopied: 'Plan kopiert!',
      soloDiningBadge: '🍜 Solo-Food Tipp',
      badgeFireworks: 'Feuerwerk & Drohnen',
      badgeEvent: 'Kultur-Event',
      badgeFoliage: 'Herbstlaub (Danpung)',
      badgeCoast: 'Incheon Meeresküste',
      badgeWinter: 'Winterzauber & Lichter',
      emptyCurated: 'Keine Wochenend-Touren für diese Filtereinstellungen gefunden. Wähle „Alle Monate“ oder „Alle Regionen“.',
      generatorTitle: 'Dein maßgeschneiderter Tagesplan',
      generatorDesc: 'Automatisch zusammengestellt aus deinen ausgewählten Tagen und Interessen.',
      guideFireworksTitle: '🎆 Seoul International Fireworks Festival (05. September 2026)',
      guideFireworksText: 'Findet am Samstag, 05. September 2026 im Yeouido Hangang Park statt (Hauptshow 20:00 - 21:10 Uhr). Eines der größten Feuerwerke der Welt. Für Solo-Besucher: Bereits vor 16 Uhr auf den Uferwiesen im Yeouido Park oder auf der Nodeul-Insel mit Picknickmatte & Powerbank einfinden. Essen & Convenience-Store-Ramen rechtzeitig holen!',
      guideBitseomTitle: '✨ Seoul Light Hangang Bitseom Festival (02. – 11. Oktober 2026)',
      guideBitseomText: 'Täglich 18:30 – 22:30 Uhr auf der Nodeul-Insel (Nodeulseom). Spektakuläres Laser- und Medienkunstfestival am Han-Fluss mit gigantischen Lichtinstallationen („Rhapsody of Light“), Light Run (빛섬런), Bitseom Lectures und Live-House-Konzerten. 100% kostenloser Eintritt! Kostenlose Shuttlebusse alle 15 Min ab Sinyongsan, Noryangjin und Nodeul Station.',
      guideNightPalaceTitle: '🏯 Gyeongbokgung Palast Nachtöffnung (Sept - Okt 2026)',
      guideNightPalaceText: '19:00 - 21:30 Uhr. Ausländer erhalten 300 Tickets täglich direkt an der Kasse gegen Vorlage des Reisepasses (3.000 KRW). Wer einen traditionellen Hanbok trägt, hat 100% FREIEN Eintritt ohne Ticket-Limit!',
      guideHonbapTitle: '🍲 Solo-Dining (Honbap / 혼밥) Survival-Tipps',
      guideHonbapText: 'In Korea ist Alleinessen bei Kimbap-Lokalen (Kimbap Cheonguk), Märkten (Gwangjang, Tongin), Nudelbars, Kaufhaus-Food-Courts (The Hyundai Seoul B1) und Jjajangmyeon-Restaurants vollkommen normal. Meide zu Stoßzeiten traditionelles Korean BBQ (Samgyeopsal) oder Dakgalbi, da dort oft mindestens 2 Portionen bestellt werden müssen.',
      closeBtn: 'Schließen',
    },
    en: {
      modalTitle: 'Weekend & Festival Planner (Sep – Dec)',
      modalSubtitle: 'Plan your Fridays, Saturdays & Sundays in Seoul, Incheon & Gyeonggi',
      tabCurated: 'Curated Weekends (9 Tours)',
      tabGenerator: 'Dynamic Tour Generator',
      tabGuide: 'Solo & Festival Guide',
      filterDaysTitle: 'Your Free Days:',
      dayFri: 'Friday (Fri)',
      daySat: 'Saturday (Sat)',
      daySun: 'Sunday (Sun)',
      quickWeekendFrSa: 'Fri + Sat (Standard)',
      quickWeekendFull: 'Fri + Sat + Sun (Full)',
      filterMonthTitle: 'Month / Season:',
      monthAll: 'All (Sep–Dec)',
      monthSep: 'September 2026',
      monthOct: 'October 2026',
      monthNov: 'November 2026',
      monthDec: 'December 2026',
      filterRegionTitle: 'Region:',
      regionAll: 'All Regions',
      regionSeoul: 'Seoul',
      regionIncheon: 'Incheon',
      regionGyeonggi: 'Gyeonggi (Suwon/Goyang)',
      filterThemeTitle: 'Focus:',
      themeAll: 'All Themes',
      themeFestivals: '🎆 Festivals & Drone Shows',
      themePalaces: '🏯 Palaces & Hanok',
      themeModern: '🏙️ Skyline & Modern',
      themeNature: '🍁 Nature & Autumn Foliage',
      themeFood: '🍲 Solo-Food & Markets',
      soloToggleLabel: '100% Solo-Friendly Mode (Hon-nol / Honbap)',
      soloToggleSub: 'Highlights spots with single-person dining and easy solo access',
      importToPlannerBtn: '⚡ Import to Route Planner',
      openNaverRoute: 'Open in Naver Map',
      openKakaoRoute: 'Open in KakaoMap',
      copyPlanBtn: 'Copy Day Plan',
      planCopied: 'Plan Copied!',
      soloDiningBadge: '🍜 Solo Food Tip',
      badgeFireworks: 'Fireworks & Drones',
      badgeEvent: 'Cultural Event',
      badgeFoliage: 'Autumn Foliage (Danpung)',
      badgeCoast: 'Incheon Coast',
      badgeWinter: 'Winter Lights',
      emptyCurated: 'No weekend tours found matching this filter. Try selecting "All Months" or "All Regions".',
      generatorTitle: 'Customized Day Schedule',
      generatorDesc: 'Generated dynamically based on your selected days and interests.',
      guideFireworksTitle: '🎆 Seoul International Fireworks Festival (Sep 5, 2026)',
      guideFireworksText: 'Held Saturday, September 5, 2026 in Yeouido Hangang Park (Main show 20:00 - 21:10). Free public admission on river lawns. Solo tip: Arrive before 16:00 on the grass lawn or Nodeul Island with a picnic mat and power bank.',
      guideBitseomTitle: '✨ Seoul Light Hangang Bitseom Festival (Oct 2 – 11, 2026)',
      guideBitseomText: 'Daily 18:30 – 22:30 at Nodeul Island. Waterfront laser and media art festival ("Rhapsody of Light"), Light Run night marathon, Bitseom Lectures, and Live House indie concerts. Free admission! Free festival shuttles run every 15 min from Sinyongsan, Noryangjin, and Nodeul stations.',
      guideNightPalaceTitle: '🏯 Gyeongbokgung Palace Night Opening (Sep - Oct 2026)',
      guideNightPalaceText: '19:00 - 21:30. 300 daily on-site foreigner tickets available at Gwanghwamun ticket box with passport (3,000 KRW). Or enjoy 100% Free Entry by wearing Hanbok!',
      guideHonbapTitle: '🍲 Solo-Dining (Honbap) Survival Guide',
      guideHonbapText: 'Dining solo is effortless at traditional markets (Gwangjang), noodle counters, Jjajangmyeon spots in Chinatown, and department store food courts (The Hyundai B1). Avoid peak-hour Korean BBQ or Dakgalbi requiring 2-person minimum orders.',
      closeBtn: 'Close',
    },
    kr: {
      modalTitle: '주말 & 축제 여행 플래너 (9월 ~ 12월)',
      modalSubtitle: '금·토·일 서울, 인천, 경기 당일치기 코스 & 축제 일정',
      tabCurated: '추천 주말 코스 (9선)',
      tabGenerator: '맞춤 일정 생성기',
      tabGuide: '혼행 & 축제 가이드',
      filterDaysTitle: '나의 휴일 선택:',
      dayFri: '금요일',
      daySat: '토요일',
      daySun: '일요일',
      quickWeekendFrSa: '금+토 (기본)',
      quickWeekendFull: '금+토+일 (전체)',
      filterMonthTitle: '월 / 시즌:',
      monthAll: '전체 (9월~12월)',
      monthSep: '2026년 9월',
      monthOct: '2026년 10월',
      monthNov: '2026년 11월',
      monthDec: '2026년 12월',
      filterRegionTitle: '지역:',
      regionAll: '전체 지역',
      regionSeoul: '서울',
      regionIncheon: '인천',
      regionGyeonggi: '경기 (수원/고양)',
      filterThemeTitle: '테마:',
      themeAll: '모든 테마',
      themeFestivals: '🎆 축제 & 드론쇼',
      themePalaces: '🏯 궁궐 & 한옥',
      themeModern: '🏙️ 스카이라인 & 현대',
      themeNature: '🍁 자연 & 단풍',
      themeFood: '🍲 혼밥 & 전통시장',
      soloToggleLabel: '100% 혼놀/혼밥 안심 모드',
      soloToggleSub: '1인 식사 가능 식당 및 혼자 가기 편한 명소 우선',
      importToPlannerBtn: '⚡ 경로 플래너에 담기',
      openNaverRoute: '네이버 지도에서 열기',
      openKakaoRoute: '카카오맵에서 열기',
      copyPlanBtn: '일정 텍스트 복사',
      planCopied: '복사 완료!',
      soloDiningBadge: '🍜 혼밥 팁',
      badgeFireworks: '불꽃 & 드론쇼',
      badgeEvent: '문화 축제',
      badgeFoliage: '단풍놀이',
      badgeCoast: '인천 바다',
      badgeWinter: '겨울 조명 축제',
      emptyCurated: '선택한 조건에 맞는 주말 코스가 없습니다. 전체 필터를 선택해보세요.',
      generatorTitle: '맞춤형 여행 일정',
      generatorDesc: '선택한 요일과 관심사에 맞춰 자동으로 구성된 최적의 경로입니다.',
      guideFireworksTitle: '🎆 서울세계불꽃축제 (2026년 9월 5일 토요일)',
      guideFireworksText: '2026년 9월 5일(토) 여의도 한강공원에서 개최(본 불꽃쇼 20:00~21:10). 한강변 무료 관람. 혼행객은 오후 4시 전 돗자리와 보조배터리를 챙겨 여의도 잔디밭이나 노들섬에 자리 잡기를 추천합니다.',
      guideBitseomTitle: '✨ 서울라이트 한강 빛섬축제 (2026.10.02 ~ 10.11)',
      guideBitseomText: '매일 18:30~22:30 노들섬 전역에서 펼쳐지는 초대형 한강 레이저·미디어아트 축제. 빛섬 3L 프로그램(레이저아트, 빛섬런, 빛섬렉처)과 라이브하우스 공연. 무료 입장! 신용산역·노량진역·노들역 순환 무료 셔틀버스(18:00~22:00, 15분 간격) 운행.',
      guideNightPalaceTitle: '🏯 경복궁 야간관람 (2026년 9월~10월)',
      guideNightPalaceText: '19:00~21:30. 외국인은 여권 지참 시 현장 매표소에서 일 300매 선착순 구매 가능(3,000원). 한복 착용자는 사전 예매 없이 100% 무료 입장!',
      guideHonbapTitle: '🍲 1인 혼밥 꿀팁 가이드',
      guideHonbapText: '광장시장 포차, 인천 차이나타운 짜장면집, 김밥천국, 더현대 서울 지하 푸드코트는 혼밥하기에 완벽합니다. 2인분 이상 주문이 필수인 전통 고깃집(삼겹살)이나 닭갈비는 피하세요.',
      closeBtn: '닫기',
    },
  }[language];

  const toggleDay = (day: Weekday) => {
    if (selectedDays.includes(day)) {
      if (selectedDays.length === 1) return; // Keep at least 1 day
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  // Filter curated weekends
  const filteredCuratedWeekends = useMemo(() => {
    return CURATED_WEEKENDS.filter((wk) => {
      if (selectedMonth !== 'all' && wk.month !== selectedMonth) return false;
      if (selectedRegion !== 'all') {
        if (selectedRegion === 'seoul' && wk.region !== 'Seoul' && wk.region !== 'Mixed') return false;
        if (selectedRegion === 'incheon' && wk.region !== 'Incheon' && wk.region !== 'Mixed') return false;
        if (selectedRegion === 'gyeonggi' && wk.region !== 'Gyeonggi' && wk.region !== 'Mixed') return false;
      }
      if (selectedTheme !== 'all') {
        if (selectedTheme === 'festivals' && wk.badgeType !== 'fireworks' && wk.badgeType !== 'event') return false;
        if (selectedTheme === 'palaces' && wk.badgeType !== 'event') return false;
        if (selectedTheme === 'nature' && wk.badgeType !== 'foliage') return false;
        if (selectedTheme === 'modern' && wk.badgeType !== 'winter' && wk.region !== 'Incheon') return false;
      }
      return true;
    });
  }, [selectedMonth, selectedRegion, selectedTheme]);

  const handleImportCuratedToPlanner = (weekend: CuratedWeekend) => {
    // Collect all spot IDs from active selected days
    const spotIdsToImport: string[] = [];
    selectedDays.forEach((dayKey) => {
      const dayData = weekend.days[dayKey];
      if (dayData && dayData.spotIds) {
        dayData.spotIds.forEach((id) => {
          if (!spotIdsToImport.includes(id)) {
            spotIdsToImport.push(id);
          }
        });
      }
    });

    if (spotIdsToImport.length > 0) {
      onImportSpotsToPlanner(spotIdsToImport);
      confetti({
        particleCount: 50,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#d7263d', '#1c1917', '#f59e0b'],
      });
      onClose();
    }
  };

  const handleCopyWeekendText = (weekend: CuratedWeekend) => {
    let output = `${weekend.titleKr} (${language === 'de' ? weekend.titleDe : language === 'kr' ? weekend.titleKr : weekend.titleEn})\n\n`;
    selectedDays.forEach((dayKey) => {
      const dayData = weekend.days[dayKey];
      if (!dayData) return;
      output += `${language === 'de' ? dayData.dayNameDe : language === 'kr' ? dayData.dayNameKr : dayData.dayNameEn}\n`;
      output += `   ${language === 'de' ? dayData.themeTitleDe : language === 'kr' ? dayData.themeTitleKr : dayData.themeTitleEn}\n`;
      dayData.schedule.forEach((item) => {
        output += `   • ${item.time}: ${language === 'de' ? item.titleDe : language === 'kr' ? item.titleKr : item.titleEn}\n`;
        output += `     ${language === 'de' ? item.tipDe : language === 'kr' ? item.tipKr : item.tipEn}\n`;
      });
      output += '\n';
    });
    output += `Solo-Tipp: ${language === 'de' ? weekend.soloHighlightDe : language === 'kr' ? weekend.soloHighlightKr : weekend.soloHighlightEn}\n`;
    output += `K-SpotFinder`;

    navigator.clipboard.writeText(output);
    setCopiedPlanId(weekend.id);
    setTimeout(() => setCopiedPlanId(null), 3000);
  };

  const handleOpenMultiMap = (spotIds: string[], provider: 'naver' | 'kakao') => {
    const spots = spotIds
      .map((id) => SPOTS_DATA.find((s) => s.id === id))
      .filter((s): s is Spot => Boolean(s));
    if (spots.length === 0) return;
    const url = generateMultiSpotRouteUrl(spots, provider);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-sheet weekend-planner-modal"
        style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header__left">
            <div className="modal-header__icon" style={{ background: 'var(--ink)' }}>
              <CalendarDays style={{ width: 22, height: 22, color: 'white' }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
                  {t.modalTitle}
                </h2>
                <span className="badge badge--neutral" style={{ fontSize: 10 }}>FR • SA • SO</span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {t.modalSubtitle}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="weekend-planner-tabs">
          <button
            onClick={() => setActiveTab('curated')}
            className={`weekend-tab-btn ${activeTab === 'curated' ? 'weekend-tab-btn--active' : ''}`}
          >
            <Sparkles style={{ width: 16, height: 16 }} />
            <span>{t.tabCurated}</span>
          </button>
          <button
            onClick={() => setActiveTab('generator')}
            className={`weekend-tab-btn ${activeTab === 'generator' ? 'weekend-tab-btn--active' : ''}`}
          >
            <Sliders style={{ width: 16, height: 16 }} />
            <span>{t.tabGenerator}</span>
          </button>
          <button
            onClick={() => setActiveTab('guide')}
            className={`weekend-tab-btn ${activeTab === 'guide' ? 'weekend-tab-btn--active' : ''}`}
          >
            <HelpCircle style={{ width: 16, height: 16 }} />
            <span>{t.tabGuide}</span>
          </button>
        </div>

        {/* Controls / Filter Bar */}
        <div className="weekend-planner-controls">
          {/* Day Selector Chips */}
          <div className="control-group">
            <span className="control-label">{t.filterDaysTitle}</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => toggleDay('fri')}
                className={`day-chip ${selectedDays.includes('fri') ? 'day-chip--active' : ''}`}
              >
                <CheckCircle2 style={{ width: 14, height: 14, opacity: selectedDays.includes('fri') ? 1 : 0.4 }} />
                <span>{t.dayFri}</span>
              </button>
              <button
                onClick={() => toggleDay('sat')}
                className={`day-chip ${selectedDays.includes('sat') ? 'day-chip--active' : ''}`}
              >
                <CheckCircle2 style={{ width: 14, height: 14, opacity: selectedDays.includes('sat') ? 1 : 0.4 }} />
                <span>{t.daySat}</span>
              </button>
              <button
                onClick={() => toggleDay('sun')}
                className={`day-chip ${selectedDays.includes('sun') ? 'day-chip--active' : ''}`}
              >
                <CheckCircle2 style={{ width: 14, height: 14, opacity: selectedDays.includes('sun') ? 1 : 0.4 }} />
                <span>{t.daySun}</span>
              </button>
              
              <div className="flex items-center gap-1 ml-auto">
                <button
                  onClick={() => setSelectedDays(['fri', 'sat'])}
                  className="quick-preset-btn"
                  title={t.quickWeekendFrSa}
                >
                  Fr+Sa
                </button>
                <button
                  onClick={() => setSelectedDays(['fri', 'sat', 'sun'])}
                  className="quick-preset-btn"
                  title={t.quickWeekendFull}
                >
                  Fr-So
                </button>
              </div>
            </div>
          </div>

          {/* Month & Region Filter Rows */}
          <div className="control-sub-row">
            {/* Months */}
            <div className="flex items-center gap-1 overflow-x-auto py-1">
              {(['all', 'sep', 'oct', 'nov', 'dec'] as MonthFilter[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setSelectedMonth(m)}
                  className={`filter-pill-sm ${selectedMonth === m ? 'filter-pill-sm--active' : ''}`}
                >
                  {m === 'all' && t.monthAll}
                  {m === 'sep' && (language === 'kr' ? '9월 (불꽃축제)' : language === 'de' ? 'Sept (Feuerwerk)' : 'Sep (Fireworks)')}
                  {m === 'oct' && (language === 'kr' ? '10월 (억새축제)' : language === 'de' ? 'Okt (Silbergras)' : 'Oct (Silver Grass)')}
                  {m === 'nov' && (language === 'kr' ? '11월 (단풍절정)' : language === 'de' ? 'Nov (Herbstlaub)' : 'Nov (Foliage)')}
                  {m === 'dec' && (language === 'kr' ? '12월 (겨울조명)' : language === 'de' ? 'Dez (Winterlichter)' : 'Dec (Winter Lights)')}
                </button>
              ))}
            </div>

            {/* Regions */}
            <div className="flex items-center gap-1 overflow-x-auto py-1">
              {(['all', 'seoul', 'incheon', 'gyeonggi'] as RegionFilter[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setSelectedRegion(r)}
                  className={`filter-pill-sm ${selectedRegion === r ? 'filter-pill-sm--active' : ''}`}
                >
                  {r === 'all' && t.regionAll}
                  {r === 'seoul' && '🏙️ Seoul'}
                  {r === 'incheon' && '🌊 Incheon'}
                  {r === 'gyeonggi' && '🏯 Suwon / Goyang'}
                </button>
              ))}
            </div>
          </div>

          {/* 100% Solo Friendly Switch */}
          <div className="solo-switch-bar">
            <label className="solo-switch-label">
              <input
                type="checkbox"
                checked={soloOnly}
                onChange={(e) => setSoloOnly(e.target.checked)}
                className="styled-checkbox"
              />
              <div>
                <span className="solo-switch-title">
                  {t.soloToggleLabel}
                </span>
                <span className="solo-switch-sub">
                  {t.soloToggleSub}
                </span>
              </div>
            </label>
            <span className="solo-status-badge">
              {soloOnly ? '✅ 100% Solo-Geprüft' : 'Alle Optionen'}
            </span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body weekend-planner-body">
          {/* TAB 1: CURATED WEEKENDS */}
          {activeTab === 'curated' && (
            <div className="curated-list">
              {filteredCuratedWeekends.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
                  <Calendar style={{ width: 36, height: 36, color: 'var(--text-muted)', margin: '0 auto 12px' }} />
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                    {t.emptyCurated}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedMonth('all');
                      setSelectedRegion('all');
                      setSelectedTheme('all');
                    }}
                    className="btn btn--secondary btn--sm"
                    style={{ marginTop: 14 }}
                  >
                    Filter zurücksetzen
                  </button>
                </div>
              ) : (
                filteredCuratedWeekends.map((wk) => (
                  <div key={wk.id} className="curated-card">
                    {/* Card Header */}
                    <div className="curated-card__header">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`curated-badge curated-badge--${wk.badgeType}`}>
                            {language === 'de' ? wk.badgeDe : language === 'kr' ? wk.badgeKr : wk.badgeEn}
                          </span>
                          <span className="curated-date-badge">
                            <CalendarDays style={{ width: 12, height: 12 }} />
                            <span>{wk.dateRange}</span>
                          </span>
                          <span className="curated-region-tag">
                            <MapPin style={{ width: 12, height: 12 }} />
                            <span>{wk.region}</span>
                          </span>
                        </div>
                        <h3 className="curated-card__title">
                          {language === 'de' ? wk.titleDe : language === 'kr' ? wk.titleKr : wk.titleEn}
                        </h3>
                        <p className="curated-card__desc">
                          {language === 'de' ? wk.descriptionDe : language === 'kr' ? wk.descriptionKr : wk.descriptionEn}
                        </p>
                      </div>
                    </div>

                    {/* Solo Highlight Box */}
                    <div className="solo-highlight-box">
                      <Utensils style={{ width: 16, height: 16, color: '#34d399', flexShrink: 0 }} />
                      <span style={{ fontSize: 12.5, color: '#d1fae5', lineHeight: 1.4 }}>
                        <strong>{t.soloDiningBadge}:</strong> {language === 'de' ? wk.soloHighlightDe : language === 'kr' ? wk.soloHighlightKr : wk.soloHighlightEn}
                      </span>
                    </div>

                    {/* Days Breakdown */}
                    <div className="curated-days-grid">
                      {selectedDays.map((dayKey) => {
                        const dayData = wk.days[dayKey];
                        if (!dayData) return null;
                        return (
                          <div key={dayKey} className="curated-day-column">
                            <div className="curated-day-header">
                              <span className="curated-day-name">
                                {language === 'de' ? dayData.dayNameDe : language === 'kr' ? dayData.dayNameKr : dayData.dayNameEn}
                              </span>
                              {dayData.dateFormattedDe && (
                                <span className="curated-day-date">
                                  📅 {language === 'de' ? dayData.dateFormattedDe : language === 'kr' ? dayData.dateFormattedKr : dayData.dateFormattedEn}
                                </span>
                              )}
                              <span className="curated-day-theme">
                                {language === 'de' ? dayData.themeTitleDe : language === 'kr' ? dayData.themeTitleKr : dayData.themeTitleEn}
                              </span>
                            </div>

                            <div className="curated-day-timeline">
                              {dayData.schedule.map((item, idx) => {
                                const linkedSpot = item.spotId ? SPOTS_DATA.find((s) => s.id === item.spotId) : null;
                                return (
                                  <div key={idx} className="timeline-item">
                                    <div className="timeline-dot" />
                                    <div className="timeline-content">
                                      <div className="timeline-time">{item.time}</div>
                                      <div className="timeline-title">
                                        {linkedSpot ? (
                                          <button
                                            onClick={() => onSelectSpot(linkedSpot)}
                                            className="spot-link-btn"
                                            title="Spot-Details öffnen"
                                          >
                                            <span>{language === 'de' ? item.titleDe : language === 'kr' ? item.titleKr : item.titleEn}</span>
                                            <ExternalLink style={{ width: 12, height: 12, opacity: 0.7 }} />
                                          </button>
                                        ) : (
                                          <span>{language === 'de' ? item.titleDe : language === 'kr' ? item.titleKr : item.titleEn}</span>
                                        )}
                                      </div>
                                      <div className="timeline-tip">
                                        {language === 'de' ? item.tipDe : language === 'kr' ? item.tipKr : item.tipEn}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Bar */}
                    <div className="curated-card__actions">
                      <button
                        onClick={() => handleImportCuratedToPlanner(wk)}
                        className="btn btn--primary btn--sm"
                        style={{
                          background: 'var(--ink)',
                          color: '#ffffff',
                          fontWeight: 700,
                        }}
                      >
                        <Sparkles style={{ width: 15, height: 15 }} />
                        <span>{t.importToPlannerBtn}</span>
                      </button>

                      {/* Map Openers */}
                      {wk.days[selectedDays[0]] && wk.days[selectedDays[0]].spotIds.length > 0 && (
                        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                          <button
                            onClick={() => handleOpenMultiMap(wk.days[selectedDays[0]].spotIds, 'naver')}
                            className="btn btn--naver btn--sm"
                            title={t.openNaverRoute}
                          >
                            <span>Naver Map</span>
                            <ExternalLink style={{ width: 13, height: 13 }} />
                          </button>
                          <button
                            onClick={() => handleOpenMultiMap(wk.days[selectedDays[0]].spotIds, 'kakao')}
                            className="btn btn--kakao btn--sm"
                            title={t.openKakaoRoute}
                          >
                            <span>KakaoMap</span>
                            <ExternalLink style={{ width: 13, height: 13 }} />
                          </button>
                          <button
                            onClick={() => handleCopyWeekendText(wk)}
                            className="btn btn--ghost btn--sm"
                            title={t.copyPlanBtn}
                          >
                            {copiedPlanId === wk.id ? (
                              <Check style={{ width: 14, height: 14, color: 'var(--success)' }} />
                            ) : (
                              <Copy style={{ width: 14, height: 14 }} />
                            )}
                            <span>{copiedPlanId === wk.id ? t.planCopied : t.copyPlanBtn}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB 2: DYNAMIC GENERATOR */}
          {activeTab === 'generator' && (() => {
            const currentWeekend = CURATED_WEEKENDS.find((w) => w.id === selectedGeneratorWeekendId) || CURATED_WEEKENDS[0];

            return (
              <div className="generator-view">
                <div className="generator-header-box">
                  <Sliders style={{ width: 22, height: 22, color: 'var(--accent)', flexShrink: 0 }} />
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                      {t.generatorTitle}
                    </h3>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {language === 'de'
                        ? 'Wähle ein konkretes Reise-Wochenende aus. Alle Stationen & Festivals sind exakt auf das reale Datum und den jeweiligen Wochentag abgestimmt.'
                        : language === 'kr'
                        ? '여행할 주말을 선택하세요. 모든 명소와 축제 일정은 실제 요일 및 날짜에 정확히 매칭됩니다.'
                        : 'Select a travel weekend. All sights and festivals are strictly matched to the real calendar day and date.'}
                    </p>
                  </div>
                </div>

                {/* Weekend & Calendar Date Selector */}
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>
                    📅 {language === 'de' ? 'Wähle dein Reise-Wochenende (Sept – Dez 2026):' : language === 'kr' ? '여행 주말 및 날짜 선택 (2026년 9월~12월):' : 'Choose Weekend & Travel Date (Sep–Dec 2026):'}
                  </label>
                  <div className="generator-weekend-selector">
                    {CURATED_WEEKENDS.map((wk) => {
                      const isActive = selectedGeneratorWeekendId === wk.id;
                      return (
                        <button
                          key={wk.id}
                          onClick={() => setSelectedGeneratorWeekendId(wk.id)}
                          className={`generator-weekend-pill ${isActive ? 'generator-weekend-pill--active' : ''}`}
                        >
                          <span>{language === 'de' ? wk.badgeDe : language === 'kr' ? wk.badgeKr : wk.badgeEn}</span>
                          <span className="generator-weekend-pill__sub">📅 {wk.dateRange}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Days Breakdown for the chosen weekend */}
                <div className="generator-days-list">
                  {selectedDays.map((dayKey, dayIndex) => {
                    const dayData = currentWeekend.days[dayKey];
                    if (!dayData) return null;

                    const dayTitle = dayKey === 'fri' ? t.dayFri : dayKey === 'sat' ? t.daySat : t.daySun;
                    const dateFormatted = language === 'de' ? dayData.dateFormattedDe : language === 'kr' ? dayData.dateFormattedKr : dayData.dateFormattedEn;
                    const themeTitle = language === 'de' ? dayData.themeTitleDe : language === 'kr' ? dayData.themeTitleKr : dayData.themeTitleEn;

                    return (
                      <div key={dayKey} className="generator-day-card">
                        <div className="generator-day-card__header">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="curated-pill-tag">Tag {dayIndex + 1} ({dayTitle})</span>
                            {dateFormatted && (
                              <span className="curated-day-date">
                                {dateFormatted}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
                            {themeTitle}
                          </span>
                        </div>

                        <div className="generator-spots-list">
                          {dayData.schedule.map((item, sIdx) => {
                            const linkedSpot = item.spotId ? SPOTS_DATA.find((s) => s.id === item.spotId) : null;
                            const links = linkedSpot ? getSpotMapLinks(linkedSpot, userLocation) : null;

                            return (
                              <div key={sIdx} className="generator-spot-row">
                                <div className="generator-spot-time">
                                  <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{item.time}</span>
                                  {item.isSoloFood && (
                                    <span style={{ fontSize: 10, color: 'var(--success)', fontWeight: 700 }}>Solo Food</span>
                                  )}
                                </div>
                                <div className="generator-spot-info">
                                  <div className="generator-spot-title">
                                    {linkedSpot ? (
                                      <button
                                        onClick={() => onSelectSpot(linkedSpot)}
                                        className="spot-link-btn"
                                        title="Details öffnen"
                                      >
                                        <span>{language === 'de' ? item.titleDe : language === 'kr' ? item.titleKr : item.titleEn}</span>
                                        <ExternalLink style={{ width: 12, height: 12, opacity: 0.7 }} />
                                      </button>
                                    ) : (
                                      <span>{language === 'de' ? item.titleDe : language === 'kr' ? item.titleKr : item.titleEn}</span>
                                    )}
                                  </div>
                                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                                    {language === 'de' ? item.tipDe : language === 'kr' ? item.tipKr : item.tipEn}
                                  </span>
                                  {linkedSpot && (
                                    <span className="generator-spot-subway" style={{ marginTop: 2 }}>
                                      🚇 {linkedSpot.subwayInfo}
                                    </span>
                                  )}
                                </div>
                                {links && (
                                  <div className="generator-spot-links">
                                    <a
                                      href={links.naverWeb}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-pill-naver"
                                      title="Naver Map"
                                    >
                                      N
                                    </a>
                                    <a
                                      href={links.kakaoWeb}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn-pill-kakao"
                                      title="KakaoMap"
                                    >
                                      K
                                    </a>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Day Action Buttons */}
                        {dayData.spotIds && dayData.spotIds.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => {
                                onImportSpotsToPlanner(dayData.spotIds);
                                confetti({ particleCount: 40, spread: 60, origin: { y: 0.6 } });
                                onClose();
                              }}
                              className="btn btn--primary btn--sm"
                              style={{ background: 'var(--ink)', color: '#ffffff', fontWeight: 700 }}
                            >
                              <Sparkles style={{ width: 13, height: 13 }} />
                              <span>{language === 'de' ? 'Diesen Tag in Routenplaner' : language === 'kr' ? '이 일정 플래너에 담기' : 'Import day to planner'}</span>
                            </button>
                            <button
                              onClick={() => handleOpenMultiMap(dayData.spotIds, 'naver')}
                              className="btn btn--naver btn--sm"
                            >
                              <span>Naver Map Route</span>
                              <ExternalLink style={{ width: 12, height: 12 }} />
                            </button>
                            <button
                              onClick={() => handleOpenMultiMap(dayData.spotIds, 'kakao')}
                              className="btn btn--kakao btn--sm"
                            >
                              <span>KakaoMap Route</span>
                              <ExternalLink style={{ width: 12, height: 12 }} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* TAB 3: SOLO & FESTIVAL GUIDE */}
          {activeTab === 'guide' && (
            <div className="guide-view">
              {/* Fireworks Special Box */}
              <div className="guide-card guide-card--fireworks">
                <div className="guide-card__header">
                  <Flame style={{ width: 22, height: 22, color: '#f59e0b' }} />
                  <h4 style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24' }}>
                    {t.guideFireworksTitle}
                  </h4>
                </div>
                <p className="guide-card__text">
                  {t.guideFireworksText}
                </p>
                <div className="guide-tips-grid">
                  <div className="guide-tip-pill">
                    📍 <strong>Beste Aussicht:</strong> Mapo-Brücke, Nodeul Island, Yeouido Wiesen
                  </div>
                  <div className="guide-tip-pill">
                    ⏰ <strong>Uhrzeit:</strong> 13:00 Einlass, 20:00 - 21:10 Uhr Show
                  </div>
                  <div className="guide-tip-pill">
                    🎒 <strong>Mitbringen:</strong> Picknickmatte, Powerbank, warme Jacke
                  </div>
                  <div className="guide-tip-pill">
                    🚇 <strong>Rückweg-Tipp:</strong> Nicht Yeouinaru nutzen (überfüllt), sondern zu Fuß nach Saetgang oder Singil gehen!
                  </div>
                </div>
              </div>

              {/* Bitseom Festival Special Box */}
              <div className="guide-card" style={{ borderColor: 'rgba(56, 189, 248, 0.4)', background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)' }}>
                <div className="guide-card__header">
                  <Sparkles style={{ width: 22, height: 22, color: '#38bdf8' }} />
                  <h4 style={{ fontSize: 16, fontWeight: 800, color: '#38bdf8' }}>
                    {t.guideBitseomTitle}
                  </h4>
                </div>
                <p className="guide-card__text">
                  {t.guideBitseomText}
                </p>
                <div className="guide-tips-grid">
                  <div className="guide-tip-pill">
                    📍 <strong>{language === 'kr' ? '장소' : language === 'de' ? 'Ort' : 'Location'}:</strong> Nodeul Island (노들섬)
                  </div>
                  <div className="guide-tip-pill">
                    ⏰ <strong>{language === 'kr' ? '시간' : language === 'de' ? 'Zeiten' : 'Hours'}:</strong> 18:30 – 22:30 KST (02. – 11. Okt)
                  </div>
                  <div className="guide-tip-pill">
                    🚌 <strong>{language === 'kr' ? '셔틀버스' : language === 'de' ? 'Gratis-Shuttle' : 'Free Shuttle'}:</strong> Sinyongsan (Exit 4) / Nodeul / Noryangjin
                  </div>
                  <div className="guide-tip-pill">
                    🎟️ <strong>{language === 'kr' ? '입장료' : language === 'de' ? 'Eintritt' : 'Admission'}:</strong> 100% {language === 'kr' ? '무료' : language === 'de' ? 'Kostenlos' : 'Free'}
                  </div>
                </div>
              </div>

              {/* Night Palace Box */}
              <div className="guide-card">
                <div className="guide-card__header">
                  <Moon style={{ width: 20, height: 20, color: '#a78bfa' }} />
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.guideNightPalaceTitle}
                  </h4>
                </div>
                <p className="guide-card__text">
                  {t.guideNightPalaceText}
                </p>
              </div>

              {/* Honbap Solo Dining Guide */}
              <div className="guide-card">
                <div className="guide-card__header">
                  <Utensils style={{ width: 20, height: 20, color: '#34d399' }} />
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.guideHonbapTitle}
                  </h4>
                </div>
                <p className="guide-card__text">
                  {t.guideHonbapText}
                </p>
                <div className="korean-phrases-box">
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                    Nützliche koreanische Sätze für Solo-Travel:
                  </span>
                  <div className="phrase-row">
                    <span className="phrase-kr">"혼자 왔어요."</span>
                    <span className="phrase-rom">(Honja wa-sseoyo)</span>
                    <span className="phrase-de">Ich bin alleine / für eine Person.</span>
                  </div>
                  <div className="phrase-row">
                    <span className="phrase-kr">"한 명이요."</span>
                    <span className="phrase-rom">(Han-myeong-i-yo)</span>
                    <span className="phrase-de">Für eine Person bitte.</span>
                  </div>
                  <div className="phrase-row">
                    <span className="phrase-kr">"포장해 주세요."</span>
                    <span className="phrase-rom">(Pojang-hae ju-se-yo)</span>
                    <span className="phrase-de">Zum Mitnehmen bitte.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', padding: '12px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn--secondary btn--md">
            {t.closeBtn}
          </button>
        </div>
      </div>
    </div>
  );
};
